import React, {
    useState,
    useEffect,
    useMemo,
    useCallback,
    forwardRef,
    useRef
} from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    Chart as ChartJS,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    ChartData,
    ChartOptions,
    ArcElement,
    TooltipModel,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
    format,
    subDays,
    startOfDay,
    endOfDay,
    isEqual,
    eachDayOfInterval 
} from 'date-fns';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer";

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import InputAdornment from '@mui/material/InputAdornment';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import ListSubheader from '@mui/material/ListSubheader';
import Chip from '@mui/material/Chip'; 
import Divider from '@mui/material/Divider';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import Popper from '@mui/material/Popper';
import Fade from '@mui/material/Fade';
import { useTheme } from '@mui/material/styles';

import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

import queryGraphql from '../../helpers/ApiInterface';

// IMPORT THE TRACES SECTION
import LLMTracesSection from './LLMTracesSection';

interface KpiCardProps {
    title: string;
    value: string;
    change: string;
    isPositive: boolean;
    icon: React.ReactElement;
}

const KpiCard: React.FC<KpiCardProps> = ({ title, value, change, isPositive, icon }) => {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2, 
                display: 'flex',
                flexDirection: 'column', 
                alignItems: 'center',    
                justifyContent: 'center',
                borderRadius: '12px',
                backgroundColor: '#F9FBFC',
                height: '100%',
                textAlign: 'center',     
                gap: 0.5,                
            }}
        >
            <Box sx={{
                p: 0.5,
                bgcolor: isPositive ? 'rgba(46, 184, 92, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                {React.cloneElement(icon, { sx: { color: isPositive ? 'success.main' : 'error.main', fontSize: '24px' } })}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h6" sx={{ fontFamily: 'Gilroy-Bold' }}>
                    {value}
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: 'Gilroy-SemiBold', color: isPositive ? 'success.main' : 'error.main' }}>
                    {change}
                </Typography>
            </Box>
            <Typography variant="body2" sx={{
                fontFamily: 'Gilroy-Medium',
                color: 'text.secondary',
            }}>
                {title}
            </Typography>
        </Paper>
    );
};

ChartJS.register(
    TimeScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

interface HealthStatus {
    healthy: number;
    unhealthy: number;
}
interface ServiceData {
    id: string;
    name: string;
    health: HealthStatus;
    applicationId: string;
    appName?: string;
    clusterName?: string;
}
interface ApplicationData {
    id: string;
    name: string;
    health: HealthStatus; 
    clusterId: string;
    services: ServiceData[];
    clusterName?: string;
}
interface ClusterData {
    id: string;
    name: string;
    health: HealthStatus;
    applications: ApplicationData[];
}
interface DateRange {
    startDate: Date | null;
    endDate: Date | null;
}
interface TimeChartDataPoint {
    x: number;
    y: number;
}

interface ServiceHealthMetrics {
    cpuUsage: number[];
    memoryUsage: number[];
    maxCpu: number;
    maxMemory: number;
}

interface ExtendedServiceData extends ServiceData {
    podStatus: string;
    replicas: string;
    deploymentStatus: 'Updated';
    healthMetrics?: ServiceHealthMetrics;
}

interface ApiStat {
  apiName: string;
  totalTransaction: string;
  totalSuccess: string;
  totalFailed: string;
  avgTat: string;
  countHighTat: string;
  errorRate: {
    value: number;
    display: string;
  };
}

const dateFilterBaseStyle = { fontFamily: "Gilroy-Regular", fontSize: "14px", textTransform: "none", px: 2, minHeight: '38px', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }, margin: '0 !important', mr: '1px !important' } as const;
const dateFilterActiveStyle = { ...dateFilterBaseStyle, backgroundColor: "black", color: "white", borderRadius: "0px", '&:hover': { backgroundColor: "dimgray" } } as const;
const dateFilterInactiveStyle = { ...dateFilterBaseStyle, backgroundColor: "white", color: "grey.700", borderRadius: "0px", } as const;
const customDateActiveStyle = { ...dateFilterBaseStyle, backgroundColor: "black", color: "white", borderRadius: "0px", border: '1px solid black', '&:hover': { backgroundColor: "dimgray" } } as const;
const customDateInactiveStyle = { ...dateFilterBaseStyle, backgroundColor: "white", color: "grey.700", borderRadius: "0px", border: '1px solid #dee2e6' } as const;
const generalOutlinedButtonStyle = { fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "6px", textTransform: "none", px: 2, py: 1, height: '38px', boxSizing: 'border-box', backgroundColor: "transparent", color: "black", border: '1px solid black', '&:hover': { backgroundColor: "rgba(0, 0, 0, 0.04)", borderColor: 'dimgray', }, '&:disabled': { backgroundColor: 'transparent', borderColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' }, } as const;
const generalContainedButtonStyle = { fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1, height: '36.5px', boxSizing: 'border-box', backgroundColor: "black", color: "white", '&:hover': { backgroundColor: "dimgray" }, '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' }, } as const;

interface CustomDatePickerInputProps {
    value?: string;
    onClick?: () => void;
    isActive: boolean;
}
const CustomDatePickerInput = forwardRef<HTMLButtonElement, CustomDatePickerInputProps>(
    ({ value, onClick, isActive }, ref) => (
        <Button ref={ref} onClick={onClick} sx={isActive ? customDateActiveStyle : customDateInactiveStyle} variant="outlined">
            {isActive && value ? value : "Custom Date"}
        </Button>
    )
);
CustomDatePickerInput.displayName = 'CustomDatePickerInput';

const generateColors = (count: number): string[] => {
    const baseColors = ['#6495ED', '#FFB74D', '#4DD0E1', '#81C784', '#BA68C8', '#FF8A65', '#7986CB', '#A1887F', '#90CAF9', '#FFCC80', '#80DEEA', '#A5D6A7'];
    const result: string[] = [];
    for (let i = 0; i < count; i++) {
        result.push(baseColors[i % baseColors.length]);
    }
    return result;
};

const generateClusterSpecificTimeData = (
    itemName: string,
    baseSeed: string,
    dailyDates: Date[],
    maxVal: number,
    minVal: number,
    itemIndex: number
): TimeChartDataPoint[] => {
    let pseudoRandomCounter = 0;
    const seed = baseSeed + itemName + String(dailyDates.length) + String(maxVal) + String(minVal) + String(itemIndex) + "v2_fix_daily_data_item";

    const pseudoRandomForItem = () => {
        let x = Math.sin(seed.charCodeAt(pseudoRandomCounter % seed.length) + pseudoRandomCounter + itemIndex * 0.1) * 10000;
        pseudoRandomCounter++;
        return x - Math.floor(x);
    };

    const data: TimeChartDataPoint[] = [];
    const valueRange = maxVal - minVal;
    const itemBaseOffset = (itemIndex * (valueRange / Math.max(1, 10))) % valueRange;

    dailyDates.forEach((date, i) => {
        const timestamp = date.getTime();
        const randomComponent = pseudoRandomForItem() * (valueRange / 2.5);
        const cyclicalComponent = Math.sin((i / Math.max(1, dailyDates.length > 1 ? dailyDates.length - 1 : 1)) * Math.PI * 2 + (itemIndex * Math.PI / 5)) * (valueRange / 4);

        let value = minVal + itemBaseOffset + randomComponent + cyclicalComponent;
        value = Math.max(minVal, Math.min(maxVal, value));
        data.push({ x: timestamp, y: parseFloat(value.toFixed(1)) });
    });
    return data;
};

// --- CUSTOM TOOLTIPS ---
interface CustomGrafanaLineTooltipContext {
    chart: ChartJS<'line', TimeChartDataPoint[], unknown>;
    tooltip: TooltipModel<'line'>;
    valueSuffix?: string;
}
const CustomHtmlLineTooltipGrafana = ({ chart, tooltip, valueSuffix = '%' }: CustomGrafanaLineTooltipContext): void => {
    let tooltipEl = chart.canvas.parentNode?.querySelector<HTMLDivElement>('div.chartjs-custom-tooltip-grafana-line');
    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.className = 'chartjs-custom-tooltip-grafana-line';
        Object.assign(tooltipEl.style, {
            background: 'rgba(255, 255, 255, 0.97)', borderRadius: '6px', color: 'black', opacity: '0',
            pointerEvents: 'none', position: 'absolute', transition: 'opacity 0.15s ease, top 0.15s ease, left 0.15s ease',
            boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.18)', border: '1px solid #e0e0e0', minWidth: '200px', zIndex: '1001'
        });
        const rootDiv = document.createElement('div');
        tooltipEl.appendChild(rootDiv);
        chart.canvas.parentNode?.appendChild(tooltipEl);
    }

    if (tooltip.opacity === 0) {
        if (tooltipEl.style.opacity !== '0') {
            tooltipEl.style.opacity = '0';
            const contentRoot = tooltipEl.querySelector('div');
            if (contentRoot && contentRoot.hasChildNodes()) ReactDOM.unmountComponentAtNode(contentRoot);
        }
        return;
    }

    const dataPoints = tooltip.dataPoints || [];
    let dateTitle = '';
    if (dataPoints.length > 0) {
        const timestamp = dataPoints[0].parsed?.x;
        if (timestamp) {
            const date = new Date(timestamp);
            if (!isNaN(date.getTime())) dateTitle = format(date, 'PPP');
        }
    }
    if (!dateTitle && tooltip.title?.length) dateTitle = tooltip.title[0];

    const bodyItems = dataPoints.map(dp => ({
        label: dp.dataset?.label || '', formattedValue: `${(dp.parsed?.y ?? 0).toFixed(1)}${valueSuffix}`,
        color: dp.dataset?.borderColor as string || '#000'
    }));

    const contentRoot = tooltipEl.querySelector('div');
    if (!contentRoot) return;

    ReactDOM.render(
        <Box sx={{ p: 1.5, fontFamily: 'Gilroy-Regular', maxWidth: '300px' }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', fontFamily: 'Gilroy-Bold', textAlign: 'left' }}>{dateTitle}</Typography>
            <Divider sx={{ mb: 1 }} />
            <Stack spacing={0.7}>
                {bodyItems.sort((a, b) => parseFloat(b.formattedValue) - parseFloat(a.formattedValue)).map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                            <Box sx={{ width: 10, height: 10, bgcolor: item.color, borderRadius: '50%', mr: 1, flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ mr: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'Gilroy-Regular' }} title={item.label}>{item.label}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium', flexShrink: 0, pl: 1, fontFamily: 'Gilroy-SemiBold' }}>{item.formattedValue}</Typography>
                    </Box>
                ))}
            </Stack>
        </Box>,
        contentRoot
    );

    const { offsetLeft: canvasOffsetX, offsetTop: canvasOffsetY } = chart.canvas;
    const chartArea = chart.chartArea;
    if (!chartArea) return;
    const caretX = tooltip.caretX, caretY = tooltip.caretY;
    const tooltipWidth = tooltipEl.offsetWidth, tooltipHeight = tooltipEl.offsetHeight;
    const tooltipPadding = 12;
    let finalLeft = canvasOffsetX + caretX + tooltipPadding, finalTop = canvasOffsetY + caretY - (tooltipHeight / 2);

    if (finalLeft + tooltipWidth + tooltipPadding > canvasOffsetX + chartArea.right) finalLeft = canvasOffsetX + caretX - tooltipWidth - tooltipPadding;
    if (finalLeft < canvasOffsetX + chartArea.left + tooltipPadding) finalLeft = canvasOffsetX + chartArea.left + tooltipPadding;
    if (finalTop < canvasOffsetY + chartArea.top + tooltipPadding) finalTop = canvasOffsetY + chartArea.top + tooltipPadding;
    if (finalTop + tooltipHeight + tooltipPadding > canvasOffsetY + chartArea.bottom) finalTop = canvasOffsetY + chartArea.bottom - tooltipHeight - tooltipPadding;

    tooltipEl.style.opacity = '1';
    tooltipEl.style.left = finalLeft + 'px';
    tooltipEl.style.top = finalTop + 'px';
};

interface CustomGrafanaBarTooltipContext {
    chart: ChartJS<'bar', TimeChartDataPoint[], unknown>;
    tooltip: TooltipModel<'bar'>;
    valueSuffix?: string;
}
const CustomHtmlBarTooltipGrafana = ({ chart, tooltip, valueSuffix = ' GB' }: CustomGrafanaBarTooltipContext): void => {
    let tooltipEl = chart.canvas.parentNode?.querySelector<HTMLDivElement>('div.chartjs-custom-tooltip-grafana-bar');
    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.className = 'chartjs-custom-tooltip-grafana-bar';
        Object.assign(tooltipEl.style, {
            background: 'rgba(255, 255, 255, 0.97)', borderRadius: '6px', color: 'black', opacity: '0',
            pointerEvents: 'none', position: 'absolute', transition: 'opacity 0.15s ease, top 0.15s ease, left 0.15s ease',
            boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.18)', border: '1px solid #e0e0e0', minWidth: '180px', zIndex: '1001'
        });
        const rootDiv = document.createElement('div');
        tooltipEl.appendChild(rootDiv);
        chart.canvas.parentNode?.appendChild(tooltipEl);
    }

    if (tooltip.opacity === 0) {
        if (tooltipEl.style.opacity !== '0') {
            tooltipEl.style.opacity = '0';
            const contentRoot = tooltipEl.querySelector('div');
            if (contentRoot && contentRoot.hasChildNodes()) ReactDOM.unmountComponentAtNode(contentRoot);
        }
        return;
    }

    const dataPoint = tooltip.dataPoints?.[0];
    if (!dataPoint) return;
    let dateTitle = '';
    const timestamp = dataPoint.parsed?.x;
    if (timestamp) {
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) dateTitle = format(date, 'PPP');
    }
    if (!dateTitle && tooltip.title?.length) dateTitle = tooltip.title[0];

    const value = dataPoint.parsed?.y;
    const formattedValue = typeof value === 'number' ? `${value.toFixed(1)}${valueSuffix}` : '';
    const label = dataPoint.dataset?.label || 'Aggregated Usage';
    const color = dataPoint.dataset?.backgroundColor as string || '#000';

    const contentRoot = tooltipEl.querySelector('div');
    if (!contentRoot) return;

    ReactDOM.render(
        <Box sx={{ p: 1.5, fontFamily: 'Gilroy-Regular', maxWidth: '300px' }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', fontFamily: 'Gilroy-Bold', textAlign: 'left' }}>{dateTitle}</Typography>
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: color, borderRadius: '2px', mr: 1, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ mr: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'Gilroy-Regular' }} title={label}>{label}</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 'medium', flexShrink: 0, pl: 1, fontFamily: 'Gilroy-SemiBold' }}>{formattedValue}</Typography>
            </Box>
        </Box>,
        contentRoot
    );

    const { offsetLeft: canvasOffsetX, offsetTop: canvasOffsetY } = chart.canvas;
    const chartArea = chart.chartArea;
    if (!chartArea) return;
    const caretX = tooltip.caretX, caretY = tooltip.caretY;
    const tooltipWidth = tooltipEl.offsetWidth, tooltipHeight = tooltipEl.offsetHeight;
    const tooltipPadding = 12;
    let finalLeft = canvasOffsetX + caretX + tooltipPadding, finalTop = canvasOffsetY + caretY - (tooltipHeight / 2);

    if (finalLeft + tooltipWidth + tooltipPadding > canvasOffsetX + chartArea.right) finalLeft = canvasOffsetX + caretX - tooltipWidth - tooltipPadding;
    if (finalLeft < canvasOffsetX + chartArea.left + tooltipPadding) finalLeft = canvasOffsetX + chartArea.left + tooltipPadding;
    if (finalTop < canvasOffsetY + chartArea.top + tooltipPadding) finalTop = canvasOffsetY + chartArea.top + tooltipPadding;
    if (finalTop + tooltipHeight + tooltipPadding > canvasOffsetY + chartArea.bottom) finalTop = canvasOffsetY + chartArea.bottom - tooltipHeight - tooltipPadding;

    tooltipEl.style.opacity = '1';
    tooltipEl.style.left = finalLeft + 'px';
    tooltipEl.style.top = finalTop + 'px';
};

interface CustomCategoricalBarTooltipContext {
    chart: ChartJS<'bar', number[], unknown>;
    tooltip: TooltipModel<'bar'>;
    valueSuffix?: string;
}
const CustomHtmlCategoricalBarTooltipGrafana = ({ chart, tooltip, valueSuffix = 's' }: CustomCategoricalBarTooltipContext): void => {
    let tooltipEl = chart.canvas.parentNode?.querySelector<HTMLDivElement>('div.chartjs-custom-tooltip-categorical-bar');
    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.className = 'chartjs-custom-tooltip-categorical-bar';
        Object.assign(tooltipEl.style, {
            background: 'rgba(255, 255, 255, 0.97)', borderRadius: '6px', color: 'black', opacity: '0',
            pointerEvents: 'none', position: 'absolute', transition: 'opacity 0.15s ease, top 0.15s ease, left 0.15s ease',
            boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.18)', border: '1px solid #e0e0e0', minWidth: '180px', zIndex: '1001'
        });
        const rootDiv = document.createElement('div');
        tooltipEl.appendChild(rootDiv);
        chart.canvas.parentNode?.appendChild(tooltipEl);
    }

    if (tooltip.opacity === 0) {
        if (tooltipEl.style.opacity !== '0') {
            tooltipEl.style.opacity = '0';
            const contentRoot = tooltipEl.querySelector('div');
            if (contentRoot && contentRoot.hasChildNodes()) ReactDOM.unmountComponentAtNode(contentRoot);
        }
        return;
    }

    const dataPoint = tooltip.dataPoints?.[0];
    if (!dataPoint) return;

    const title = tooltip.title?.[0] || dataPoint.label || '';
    const value = dataPoint.parsed?.y;
    const formattedValue = typeof value === 'number' ? `${value.toFixed(2)}${valueSuffix}` : '';
    const label = dataPoint.dataset?.label || 'Value';
    
    // Safely extract the background color for the individual bar
    let color = '#000';
    const bgColors = dataPoint.dataset?.backgroundColor;
    if (Array.isArray(bgColors)) {
        color = bgColors[dataPoint.dataIndex] as string;
    } else if (typeof bgColors === 'string') {
        color = bgColors;
    }

    const contentRoot = tooltipEl.querySelector('div');
    if (!contentRoot) return;

    ReactDOM.render(
        <Box sx={{ p: 1.5, fontFamily: 'Gilroy-Regular', maxWidth: '300px' }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', fontFamily: 'Gilroy-Bold', textAlign: 'left' }}>{title}</Typography>
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: color, borderRadius: '2px', mr: 1, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ mr: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'Gilroy-Regular' }} title={label}>{label}</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 'medium', flexShrink: 0, pl: 1, fontFamily: 'Gilroy-SemiBold' }}>{formattedValue}</Typography>
            </Box>
        </Box>,
        contentRoot
    );

    const { offsetLeft: canvasOffsetX, offsetTop: canvasOffsetY } = chart.canvas;
    const chartArea = chart.chartArea;
    if (!chartArea) return;
    const caretX = tooltip.caretX, caretY = tooltip.caretY;
    const tooltipWidth = tooltipEl.offsetWidth, tooltipHeight = tooltipEl.offsetHeight;
    const tooltipPadding = 12;
    let finalLeft = canvasOffsetX + caretX + tooltipPadding, finalTop = canvasOffsetY + caretY - (tooltipHeight / 2);

    if (finalLeft + tooltipWidth + tooltipPadding > canvasOffsetX + chartArea.right) finalLeft = canvasOffsetX + caretX - tooltipWidth - tooltipPadding;
    if (finalLeft < canvasOffsetX + chartArea.left + tooltipPadding) finalLeft = canvasOffsetX + chartArea.left + tooltipPadding;
    if (finalTop < canvasOffsetY + chartArea.top + tooltipPadding) finalTop = canvasOffsetY + chartArea.top + tooltipPadding;
    if (finalTop + tooltipHeight + tooltipPadding > canvasOffsetY + chartArea.bottom) finalTop = canvasOffsetY + chartArea.bottom - tooltipHeight - tooltipPadding;

    tooltipEl.style.opacity = '1';
    tooltipEl.style.left = finalLeft + 'px';
    tooltipEl.style.top = finalTop + 'px';
};

interface CustomGrafanaDoughnutTooltipContext {
    chart: ChartJS<'doughnut', number[], unknown>;
    tooltip: TooltipModel<'doughnut'>;
}

const CustomHtmlDoughnutTooltipGrafana = ({ chart, tooltip }: CustomGrafanaDoughnutTooltipContext): void => {
    let tooltipEl = chart.canvas.parentNode?.querySelector<HTMLDivElement>('div.chartjs-custom-tooltip-grafana-doughnut');
    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.className = 'chartjs-custom-tooltip-grafana-doughnut';
        Object.assign(tooltipEl.style, {
            background: 'rgba(255, 255, 255, 0.97)', borderRadius: '6px', color: 'black', opacity: '0',
            pointerEvents: 'none', position: 'absolute', transition: 'opacity 0.15s ease, top 0.15s ease, left 0.15s ease',
            boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.18)', border: '1px solid #e0e0e0', minWidth: '180px', zIndex: '1001'
        });
        const rootDiv = document.createElement('div');
        tooltipEl.appendChild(rootDiv);
        chart.canvas.parentNode?.appendChild(tooltipEl);
    }

    if (tooltip.opacity === 0) {
        if (tooltipEl.style.opacity !== '0') {
            tooltipEl.style.opacity = '0';
            const contentRoot = tooltipEl.querySelector('div');
            if (contentRoot && contentRoot.hasChildNodes()) ReactDOM.unmountComponentAtNode(contentRoot);
        }
        return;
    }

    const dataPoint = tooltip.dataPoints?.[0];
    if (!dataPoint) return;

    const title = dataPoint.label || '';
    const value = dataPoint.raw as number;
    const total = (dataPoint.dataset.data as number[]).reduce((a, b) => a + b, 0);
    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) + '%' : '0.0%';
    const formattedValue = `${value.toLocaleString()} (${percentage})`;
    const label = dataPoint.dataset.label || 'Count';
    const color = (dataPoint.dataset.backgroundColor as string[])[dataPoint.dataIndex] || '#000';

    const contentRoot = tooltipEl.querySelector('div');
    if (!contentRoot) return;

    ReactDOM.render(
        <Box sx={{ p: 1.5, fontFamily: 'Gilroy-Regular', maxWidth: '300px' }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', fontFamily: 'Gilroy-Bold', textAlign: 'left' }}>{title}</Typography>
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: color, borderRadius: '2px', mr: 1, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ mr: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'Gilroy-Regular' }} title={label}>{label}</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 'medium', flexShrink: 0, pl: 1, fontFamily: 'Gilroy-SemiBold' }}>{formattedValue}</Typography>
            </Box>
        </Box>,
        contentRoot
    );

    const { offsetLeft: canvasOffsetX, offsetTop: canvasOffsetY } = chart.canvas;
    const chartArea = chart.chartArea;
    if (!chartArea) return;
    const caretX = tooltip.caretX, caretY = tooltip.caretY;
    const tooltipWidth = tooltipEl.offsetWidth, tooltipHeight = tooltipEl.offsetHeight;
    const tooltipPadding = 12;
    let finalLeft = canvasOffsetX + caretX + tooltipPadding, finalTop = canvasOffsetY + caretY - (tooltipHeight / 2);

    if (finalLeft + tooltipWidth + tooltipPadding > canvasOffsetX + chartArea.right) finalLeft = canvasOffsetX + caretX - tooltipWidth - tooltipPadding;
    if (finalLeft < canvasOffsetX + chartArea.left + tooltipPadding) finalLeft = canvasOffsetX + chartArea.left + tooltipPadding;
    if (finalTop < canvasOffsetY + chartArea.top + tooltipPadding) finalTop = canvasOffsetY + chartArea.top + tooltipPadding;
    if (finalTop + tooltipHeight + tooltipPadding > canvasOffsetY + chartArea.bottom) finalTop = canvasOffsetY + chartArea.bottom - tooltipHeight - tooltipPadding;

    tooltipEl.style.opacity = '1';
    tooltipEl.style.left = finalLeft + 'px';
    tooltipEl.style.top = finalTop + 'px';
};


// Mini Bar Chart Component for Service Details List
interface MiniBarChartProps {
    data: number[];
    maxVal: number;
    barColor: string;
    hoverColor: string;
    chartTimePoints: Date[];
    metricType: 'CPU' | 'Memory';
    onBarEnter: (event: React.MouseEvent<HTMLElement>, date: Date, value: number, type: 'CPU' | 'Memory') => void;
    onBarLeave: () => void;
}

const MiniBarChart: React.FC<MiniBarChartProps> = ({ data, maxVal, barColor, hoverColor, chartTimePoints,
    metricType,
    onBarEnter,
    onBarLeave
}) => {
    if (!data || data.length === 0) {
        return <Box sx={{ height: '24px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>N/A</Typography></Box>;
    }
    const effectiveMaxVal = maxVal === 0 ? 1 : maxVal; 

    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', height: '24px', width: '100%', gap: '1px' }}>
            {data.map((val, idx) => (
                <Box
                    key={idx}
                    onMouseEnter={(e) => chartTimePoints && chartTimePoints[idx] && onBarEnter(e, chartTimePoints[idx], val, metricType)}
                    onMouseLeave={onBarLeave}
                    sx={{
                        width: `${Math.max(1.5, (100 / data.length) - 0.5)}%`,
                        maxWidth: '6px',
                        minWidth: '1.5px',
                        height: `${Math.max(2, (val / effectiveMaxVal) * 100)}%`,
                        backgroundColor: barColor,
                        display: 'inline-block',
                        cursor: 'pointer',
                        transition: 'background-color 0.1s ease-in-out',
                        '&:hover': {
                            backgroundColor: hoverColor,
                        },
                    }}
                />
            ))}
        </Box>
    );
};


const GrafanaContent: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();

    const [activeDateFilter, setActiveDateFilter] = useState<string>('last30days');
    const [dateRange, setDateRange] = useState<DateRange>(() => {
        const today = new Date();
        return { startDate: startOfDay(subDays(today, 29)), endDate: endOfDay(today) };
    });
    const [allClusters, setAllClusters] = useState<ClusterData[]>([]);
    const [availableClusterNames, setAvailableClusterNames] = useState<string[]>([]);
    const [selectedClusterNames, setSelectedClusterNames] = useState<string[]>([]);
    const [stagedClusterSelection, setStagedClusterSelection] = useState<string[]>([]);
    const [isClusterDropdownOpen, setIsClusterDropdownOpen] = useState(false);
    const [clusterSearchTerm, setClusterSearchTerm] = useState('');
    const [selectedClustersForInsights, setSelectedClustersForInsights] = useState<string[]>([]);
    const [stagedClustersForInsights, setStagedClustersForInsights] = useState<string[]>([]);
    const [isInsightsClusterDropdownOpen, setIsInsightsClusterDropdownOpen] = useState(false);
    const [insightsClusterSearchTerm, setInsightsClusterSearchTerm] = useState('');
    const [loadingData, setLoadingData] = useState(true);
    const [errorLoading, setErrorLoading] = useState<string | null>(null);
    const [expandedClusterId, setExpandedClusterId] = useState<string | null>(null);
    const [expandedApplicationId, setExpandedApplicationId] = useState<string | null>(null);
    const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const hierarchyContainerRef = useRef<HTMLDivElement | null>(null);
    const [grafanaRepoUrl, setGrafanaRepoUrl] = useState<string | null>(null);
    const [isLoadingUrl, setIsLoadingUrl] = useState<boolean>(true);
    const [urlFetchError, setUrlFetchError] = useState<string | null>(null);
    const verticalConnectorLength = "18px", branchConnectorLength = "12px", lineThickness = "1px", dashColor = "#b0b0b0", dashCycleLength = "6px", dashPercent = "66%";
    const chartHeight = '350px';

    const [selectedApplicationIdsForAppInsights, setSelectedApplicationIdsForAppInsights] = useState<string[]>([]);
    const [stagedApplicationIdsForAppInsights, setStagedApplicationIdsForAppInsights] = useState<string[]>([]);
    const [isAppInsightsAppDropdownOpen, setIsAppInsightsAppDropdownOpen] = useState(false);
    const [appInsightsAppSearchTerm, setAppInsightsAppSearchTerm] = useState('');
    const [activeApplicationIdForServiceFilter, setActiveApplicationIdForServiceFilter] = useState<string | null>(null);

    const [selectedServiceIdsForAppInsights, setSelectedServiceIdsForAppInsights] = useState<string[]>([]);
    const [stagedServiceIdsForAppInsights, setStagedServiceIdsForAppInsights] = useState<string[]>([]);
    const [isAppInsightsServiceDropdownOpen, setIsAppInsightsServiceDropdownOpen] = useState(false);
    const [appInsightsServiceSearchTerm, setAppInsightsServiceSearchTerm] = useState('');
    const [serviceListData, setServiceListData] = useState<ExtendedServiceData[]>([]);

    const DUMMY_APIS = ['userinfo', 'resource-analysis'];
    const [selectedApiForTraffic, setSelectedApiForTraffic] = useState<string>('userinfo');

    interface MiniBarTooltipInfo {
        visible: boolean;
        content: { date: string; value: string; type: string };
        anchorEl: HTMLElement | null;
    }
    const [miniBarTooltipInfo, setMiniBarTooltipInfo] = useState<MiniBarTooltipInfo>({
        visible: false,
        content: { date: '', value: '', type: '' },
        anchorEl: null,
    });

    const handleMiniBarMouseEnter = (
        event: React.MouseEvent<HTMLElement>,
        date: Date,
        value: number,
        type: 'CPU' | 'Memory'
    ) => {
        if (event.currentTarget && date) {
            setMiniBarTooltipInfo({
                visible: true,
                anchorEl: event.currentTarget,
                content: {
                    date: format(date, 'MMM d, yyyy'),
                    value: `${value.toFixed(1)}%`,
                    type: type,
                },
            });
        }
    };

    const handleMiniBarMouseLeave = () => {
        setMiniBarTooltipInfo(prev => ({ ...prev, visible: false, anchorEl: null }));
    };

    const [apiStatsData, setApiStatsData] = useState<ApiStat[]>([]);

    useEffect(() => {
        const generateApiStats = () => {
            const seed = dateRange.startDate?.getTime() || new Date().getTime();
            const pseudoRandom = (multiplier: number, max: number) => {
                const x = Math.sin(seed * multiplier) * 10000;
                return Math.floor((x - Math.floor(x)) * max);
            };

            const data = DUMMY_APIS.map((api, index) => {
                const totalTransaction = 2500 + pseudoRandom(index * 10, 2500);
                const totalFailed = Math.max(5, pseudoRandom(index * 10 + 1, totalTransaction * 0.1));
                const totalSuccess = totalTransaction - totalFailed;
                const errorRate = (totalFailed / totalTransaction) * 100;
                const avgTat = 50 + pseudoRandom(index * 10 + 2, 400);

                return {
                    apiName: api,
                    totalTransaction: totalTransaction.toLocaleString(),
                    totalSuccess: totalSuccess.toLocaleString(),
                    totalFailed: totalFailed.toLocaleString(),
                    avgTat: `${avgTat.toFixed(2)} ms`,
                    countHighTat: pseudoRandom(index * 10 + 3, 20).toString(),
                    errorRate: {
                        value: errorRate,
                        display: `${errorRate.toFixed(2)}%`,
                    },
                };
            });
            setApiStatsData(data);
        };

        if (dateRange.startDate) {
            generateApiStats();
        }
    }, [dateRange, activeDateFilter]);

    const [kpiData, setKpiData] = useState<KpiCardProps[]>([]);

    useEffect(() => {
        const generateKpiData = () => {
            const seed = dateRange.startDate?.getTime() || new Date().getTime();
            const pseudoRandom = (multiplier: number, max: number) => {
                const x = Math.sin(seed * multiplier) * 10000;
                return Math.floor((x - Math.floor(x)) * max);
            };

            const totalTransactions = 5000 + pseudoRandom(1, 5000);
            const successRateNum = 0.95 + (pseudoRandom(2, 400) / 10000);
            const totalSuccess = Math.floor(totalTransactions * successRateNum);
            const totalFailed = totalTransactions - totalSuccess;
            const avgTat = 180 + pseudoRandom(3, 70);
            const successRate = (successRateNum * 100).toFixed(1);
            const errorRate = (100 - parseFloat(successRate)).toFixed(1);

            const generateChange = (p_seed: number) => {
                const isUp = Math.random() > 0.5;
                const value = (pseudoRandom(p_seed, 150) / 10).toFixed(1);
                const trendString = isUp ? `+${value}%` : `-${value}%`;
                return { trendString, isUp };
            };

            const txChange = generateChange(4);
            const successChange = generateChange(5);
            const failedChange = generateChange(6);
            const tatChange = generateChange(7);
            const successRateChange = generateChange(8);
            const errorRateChange = generateChange(9);

            const data: KpiCardProps[] = [
                { title: 'Total Transactions', value: totalTransactions.toLocaleString(), change: txChange.trendString, isPositive: txChange.isUp, icon: <ReceiptLongIcon /> },
                { title: 'Total Success', value: totalSuccess.toLocaleString(), change: successChange.trendString, isPositive: successChange.isUp, icon: <CheckCircleOutlineIcon /> },
                { title: 'Total Failed', value: totalFailed.toLocaleString(), change: failedChange.trendString, isPositive: !failedChange.isUp, icon: <HighlightOffIcon /> },
                { title: 'Avg. TAT', value: `${avgTat}ms`, change: tatChange.trendString, isPositive: !tatChange.isUp, icon: <AccessTimeIcon /> },
                { title: 'Success Rate', value: `${successRate}%`, change: successRateChange.trendString, isPositive: successRateChange.isUp, icon: <TrendingUpIcon /> },
                { title: 'Error Rate', value: `${errorRate}%`, change: errorRateChange.trendString, isPositive: !errorRateChange.isUp, icon: <TrendingDownIcon /> },
            ];
            setKpiData(data);
        };

        if (dateRange.startDate) {
            generateKpiData();
        }
    }, [dateRange, activeDateFilter]);

    useEffect(() => {
        const fetchGrafanaUrl = async () => {
            setIsLoadingUrl(true); setUrlFetchError(null);
            const gqlQuery = ` query GetIntegrations { getIntegrations { grafana { enabled repoUrl } } } `;
            try {
                const response = await queryGraphql(gqlQuery);
                const fetchedUrl = response.getIntegrations?.grafana?.repoUrl;
                if (fetchedUrl && fetchedUrl !== "Loading...") setGrafanaRepoUrl(fetchedUrl);
                else { setGrafanaRepoUrl(null); if (fetchedUrl === "Loading...") console.warn("Grafana repoUrl is 'Loading...'."); }
            } catch (err: any) {
                console.error("Failed to fetch Grafana repo URL:", err); setUrlFetchError(err.message || "Failed to load Grafana URL."); setGrafanaRepoUrl(null);
            } finally { setIsLoadingUrl(false); }
        };
        fetchGrafanaUrl();
    }, []);

    const chartTimePoints = useMemo((): Date[] => { if (!dateRange.startDate || !dateRange.endDate || dateRange.startDate > dateRange.endDate) return []; return eachDayOfInterval({ start: dateRange.startDate, end: dateRange.endDate }); }, [dateRange.startDate, dateRange.endDate]);

    useEffect(() => {
        setLoadingData(true); setErrorLoading(null);

        const generateAlwaysHealthyStatus = (itemNameSeed: string): HealthStatus => {
            let hash = 0;
            for (let i = 0; i < itemNameSeed.length; i++) {
                hash = (hash << 5) - hash + itemNameSeed.charCodeAt(i);
                hash |= 0;
            }
            const healthyPercentage = 90 + (Math.abs(hash) % 11);
            return { healthy: healthyPercentage, unhealthy: 100 - healthyPercentage };
        };
        const FULLEST_HEALTH_STATUS: HealthStatus = { healthy: 100, unhealthy: 0 };
       const clusters: ClusterData[] = [
            {
                id: 'c2',
                name: 'axios',
                health: FULLEST_HEALTH_STATUS,
                applications: [
                    {
                        id: 'c2_app1',
                        name: 'vertex',
                        health: FULLEST_HEALTH_STATUS,
                        clusterId: 'c2',
                        clusterName: 'axios',
                        services: [
                            { id: 'c2_app1_svc1', name: 'axiosservice', health: generateAlwaysHealthyStatus('svc-axiosservice-vertex'), applicationId: 'c2_app1' },
                        ],
                    },
                    {
                        id: 'c2_app2',
                        name: 'cipher',
                        health: FULLEST_HEALTH_STATUS,
                        clusterId: 'c2',
                        clusterName: 'axios',
                        services: [
                            { id: 'c2_app2_svc1', name: 'data-migration', health: generateAlwaysHealthyStatus('svc-data-migration-cipher'), applicationId: 'c2_app2' },
                            { id: 'c2_app2_svc2', name: 'cloud-migration', health: generateAlwaysHealthyStatus('svc-cloud-migration-cipher'), applicationId: 'c2_app2' },
                        ],
                    },
                ],
            },
            {
                id: 'c1',
                name: 'futurify',
                health: FULLEST_HEALTH_STATUS,
                applications: [
                    {
                        id: 'c1_app1',
                        name: 'cortex',
                        health: FULLEST_HEALTH_STATUS,
                        clusterId: 'c1',
                        clusterName: 'futurify',
                        services: [
                            { id: 'c1_app1_svc1', name: 'futurifyservice', health: generateAlwaysHealthyStatus('svc-futurifyservice-cortex'), applicationId: 'c1_app1' },
                            { id: 'c1_app1_svc2', name: 'frontend', health: generateAlwaysHealthyStatus('svc-frontend-cortex'), applicationId: 'c1_app1' },
                        ],
                    },
                    {
                        id: 'c1_app2',
                        name: 'nexus',
                        health: FULLEST_HEALTH_STATUS,
                        clusterId: 'c1',
                        clusterName: 'futurify',
                        services: [
                            { id: 'c1_app2_svc1', name: 'usermgmt', health: generateAlwaysHealthyStatus('svc-usermgmt-nexus'), applicationId: 'c1_app2' },
                            { id: 'c1_app2_svc2', name: 'eventmgmt', health: generateAlwaysHealthyStatus('svc-eventmgmt-nexus'), applicationId: 'c1_app2' },
                        ],
                    },
                ],
            },
        ];

        setAllClusters(clusters);
        const currentAvailableNames = clusters.map(c => c.name);
        setAvailableClusterNames(currentAvailableNames);

        const initialSelectionForOverview: string[] = currentAvailableNames;
        const currentSelectedAreValid = selectedClusterNames.every(name => currentAvailableNames.includes(name));
        if (selectedClusterNames.length === 0 || !currentSelectedAreValid) {
            setSelectedClusterNames(initialSelectionForOverview);
            setStagedClusterSelection(initialSelectionForOverview);
        } else {
            setStagedClusterSelection([...selectedClusterNames]);
        }

        setExpandedClusterId(null);
        setExpandedApplicationId(null);
        setLoadingData(false);
    }, []);

    useEffect(() => {
        const validClustersForInsights = selectedClusterNames.filter(name => availableClusterNames.includes(name));
        const overviewSelectionChanged = !validClustersForInsights.every(name => selectedClustersForInsights.includes(name)) ||
                                        selectedClustersForInsights.length !== validClustersForInsights.length;

        if (overviewSelectionChanged || (selectedClustersForInsights.length === 0 && validClustersForInsights.length > 0)) {
            setSelectedClustersForInsights(validClustersForInsights);
            setStagedClustersForInsights(validClustersForInsights);
        } else if (selectedClusterNames.length === 0 && selectedClustersForInsights.length > 0) {
            setSelectedClustersForInsights([]);
            setStagedClustersForInsights([]);
        }
    }, [selectedClusterNames, availableClusterNames]);

    const availableApplicationsForAppInsights = useMemo((): ApplicationData[] => {
        if (!selectedClustersForInsights.length || !allClusters.length) return [];
        const apps: ApplicationData[] = [];
        selectedClustersForInsights.forEach(clusterNameFromInsights => {
            const cluster = allClusters.find(c => c.name === clusterNameFromInsights);
            if (cluster) apps.push(...cluster.applications.map(app => ({ ...app, clusterName: cluster.name })));
        });
        return apps.sort((a, b) => a.name.localeCompare(b.name));
    }, [selectedClustersForInsights, allClusters]);

     useEffect(() => {
        const allAvailableAppIds = availableApplicationsForAppInsights.map(app => app.id);
        let newSelectedAppIds = selectedApplicationIdsForAppInsights.filter(id => allAvailableAppIds.includes(id));

        if (newSelectedAppIds.length === 0 && allAvailableAppIds.length > 0) {
            newSelectedAppIds = [...allAvailableAppIds];
        }
        
        if (newSelectedAppIds.length !== selectedApplicationIdsForAppInsights.length || !newSelectedAppIds.every(id => selectedApplicationIdsForAppInsights.includes(id))) {
            setSelectedApplicationIdsForAppInsights(newSelectedAppIds);
            setStagedApplicationIdsForAppInsights(newSelectedAppIds);
        }

        if (newSelectedAppIds.length > 0) {
            if (!activeApplicationIdForServiceFilter || !newSelectedAppIds.includes(activeApplicationIdForServiceFilter)) {
                setActiveApplicationIdForServiceFilter(newSelectedAppIds[0]);
            }
        } else {
            setActiveApplicationIdForServiceFilter(null);
        }
    }, [availableApplicationsForAppInsights]);

    const filteredApplicationsForAppInsightsDropdown = useMemo((): ApplicationData[] => {
        return availableApplicationsForAppInsights.filter((app: ApplicationData) =>
            `${app.name} (${app.clusterName || ''})`.toLowerCase().includes(appInsightsAppSearchTerm.toLowerCase())
        );
    }, [availableApplicationsForAppInsights, appInsightsAppSearchTerm]);

    const availableServicesForAppInsights = useMemo((): ServiceData[] => {
        if (!activeApplicationIdForServiceFilter) return [];
        const sourceApp = availableApplicationsForAppInsights.find(app => app.id === activeApplicationIdForServiceFilter);
        return sourceApp?.services.map(s => ({ ...s, appName: sourceApp.name, clusterName: sourceApp.clusterName })) || [];
    }, [activeApplicationIdForServiceFilter, availableApplicationsForAppInsights]);

    const filteredServicesForAppInsightsDropdown = useMemo(() => {
        return availableServicesForAppInsights.filter(service =>
            service.name.toLowerCase().includes(appInsightsServiceSearchTerm.toLowerCase())
        );
    }, [availableServicesForAppInsights, appInsightsServiceSearchTerm]);

    useEffect(() => {
        const allServiceIdsForActiveApp = availableServicesForAppInsights.map(s => s.id);
        let newSelectedServiceIds = selectedServiceIdsForAppInsights.filter(id => allServiceIdsForActiveApp.includes(id));

        if (activeApplicationIdForServiceFilter) {
            if (newSelectedServiceIds.length === 0 && allServiceIdsForActiveApp.length > 0) {
                newSelectedServiceIds = [...allServiceIdsForActiveApp];
            }
        } else {
             newSelectedServiceIds = [];
        }
        
        if (newSelectedServiceIds.length !== selectedServiceIdsForAppInsights.length || !newSelectedServiceIds.every(id => selectedServiceIdsForAppInsights.includes(id))) {
            setSelectedServiceIdsForAppInsights(newSelectedServiceIds);
            setStagedServiceIdsForAppInsights(newSelectedServiceIds);
        }
    }, [activeApplicationIdForServiceFilter, availableServicesForAppInsights]);


    useEffect(() => {
        if (activeApplicationIdForServiceFilter && chartTimePoints.length > 0) {
            const baseSeedForServiceDetails = activeDateFilter + (dateRange.startDate?.toISOString() || '') + (dateRange.endDate?.toISOString() || '');
            let servicePrngCounter = 0;
            const pseudoRandomForServiceDetail = (serviceIdPart: string) => {
                const detailSeed = baseSeedForServiceDetails + activeApplicationIdForServiceFilter + serviceIdPart + servicePrngCounter;
                let x = Math.sin(detailSeed.charCodeAt(servicePrngCounter % (detailSeed.length || 1)) + servicePrngCounter * 0.3) * 10000;
                servicePrngCounter++;
                return x - Math.floor(x);
            };

            const servicesToDisplayDetails = availableServicesForAppInsights.filter(s =>
                selectedServiceIdsForAppInsights.includes(s.id)
            );

            const processed = servicesToDisplayDetails.map((service) => {
                servicePrngCounter = 0;

                const podCount = Math.floor(pseudoRandomForServiceDetail(service.id + 'pods') * 2) + 2;
                const podStatus = `${podCount}/${podCount} Ready`;
                const replicas = `${podCount}/${podCount}`;

                const cpuDataPoints = chartTimePoints.map(() => parseFloat((pseudoRandomForServiceDetail(service.id + 'cpu') * 70).toFixed(1)));
                const memoryDataPoints = chartTimePoints.map(() => parseFloat((pseudoRandomForServiceDetail(service.id + 'mem') * 60).toFixed(1)));

                return {
                    ...service,
                    podStatus,
                    replicas,
                    deploymentStatus: 'Updated' as 'Updated',
                    healthMetrics: {
                        cpuUsage: cpuDataPoints,
                        memoryUsage: memoryDataPoints,
                        maxCpu: 100,
                        maxMemory: 100,
                    }
                } as ExtendedServiceData;
            });
            setServiceListData(processed);
        } else {
            setServiceListData([]);
        }
    }, [
        activeApplicationIdForServiceFilter,
        availableServicesForAppInsights,
        selectedServiceIdsForAppInsights,
        chartTimePoints,
        dateRange,
        activeDateFilter
    ]);

    const handleDateFilterClick = (filterType: string, days?: number | 'yesterday') => {
        setActiveDateFilter(filterType); const today = new Date(); let start: Date, end: Date = endOfDay(today);
        if (days === 'yesterday') { start = startOfDay(subDays(today, 1)); end = endOfDay(subDays(today, 1)); }
        else if (typeof days === 'number') start = startOfDay(subDays(today, days - 1));
        else start = startOfDay(subDays(today, 29));
        if (!dateRange.startDate || !dateRange.endDate || !isEqual(start, dateRange.startDate) || !isEqual(end, dateRange.endDate)) {
            setDateRange({ startDate: start, endDate: end });
        }
    };

    const handleCustomDateChange = (dates: [Date | null, Date | null] | Date | null) => {
        let newStartDate: Date | null = null, newEndDate: Date | null = null;
        if (Array.isArray(dates)) { const [start, end] = dates; newStartDate = start ? startOfDay(start) : null; newEndDate = end ? endOfDay(end) : null; }
        else if (dates) { newStartDate = startOfDay(dates); newEndDate = endOfDay(dates); }
        if (newStartDate) {
            setActiveDateFilter('custom');
            if (newEndDate && newEndDate < newStartDate) newEndDate = endOfDay(newStartDate);
            if (!newEndDate && newStartDate) newEndDate = endOfDay(newStartDate);

            if (!dateRange.startDate || !isEqual(newStartDate, dateRange.startDate) || (dateRange.endDate !== newEndDate && (!dateRange.endDate || !newEndDate || !isEqual(newEndDate, dateRange.endDate)))) {
                setDateRange({ startDate: newStartDate, endDate: newEndDate });
            }
        }
    };

    
    const handleClusterDropdownOpen = () => { setClusterSearchTerm(''); setStagedClusterSelection([...selectedClusterNames]); setIsClusterDropdownOpen(true); };
    const handleClusterDropdownClose = () => setIsClusterDropdownOpen(false);
    const handleStagedClusterSelectionChange = (clusterName: string) => {
        setStagedClusterSelection(prev => {
            const newSelection = prev.includes(clusterName) ? prev.filter(name => name !== clusterName) : [...prev, clusterName];
            return (newSelection.length > 6 && !prev.includes(clusterName)) ? prev : newSelection;
        });
    };
    const handleApplyClusterSelection = () => { setSelectedClusterNames(stagedClusterSelection); handleClusterDropdownClose(); };
    const handleClusterSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => setClusterSearchTerm(event.target.value);

    const handleInsightsClusterDropdownOpen = () => { setInsightsClusterSearchTerm(''); setStagedClustersForInsights([...selectedClustersForInsights]); setIsInsightsClusterDropdownOpen(true); };
    const handleInsightsClusterDropdownClose = () => setIsInsightsClusterDropdownOpen(false);
    const handleStagedInsightsClusterSelectionChange = (clusterName: string) => {
        setStagedClustersForInsights(prev => prev.includes(clusterName) ? prev.filter(name => name !== clusterName) : [...prev, clusterName]);
    };
    const handleApplyInsightsClusterSelection = () => { setSelectedClustersForInsights(stagedClustersForInsights); handleInsightsClusterDropdownClose(); };
    const handleInsightsClusterSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => setInsightsClusterSearchTerm(event.target.value);

    const handleAppInsightsAppDropdownOpen = () => { setAppInsightsAppSearchTerm(''); setStagedApplicationIdsForAppInsights([...selectedApplicationIdsForAppInsights]); setIsAppInsightsAppDropdownOpen(true); };
    const handleAppInsightsAppDropdownClose = () => setIsAppInsightsAppDropdownOpen(false);
    const handleStagedAppInsightsAppSelectionChange = (appId: string) => {
        setStagedApplicationIdsForAppInsights(prev => prev.includes(appId) ? prev.filter(id => id !== appId) : [...prev, appId]);
    };
    const handleApplyAppInsightsAppSelection = () => {
        setSelectedApplicationIdsForAppInsights(stagedApplicationIdsForAppInsights);
        handleAppInsightsAppDropdownClose();
    };
    const handleAppInsightsAppSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => setAppInsightsAppSearchTerm(event.target.value);
    const handleActiveAppChipClick = (appId: string) => setActiveApplicationIdForServiceFilter(appId);


    const handleAppInsightsServiceDropdownOpen = () => { setAppInsightsServiceSearchTerm(''); setStagedServiceIdsForAppInsights([...selectedServiceIdsForAppInsights]); setIsAppInsightsServiceDropdownOpen(true); };
    const handleAppInsightsServiceDropdownClose = () => setIsAppInsightsServiceDropdownOpen(false);
    const handleStagedAppInsightsServiceSelectionChange = (serviceId: string) => {
        setStagedServiceIdsForAppInsights(prev => prev.includes(serviceId) ? prev.filter(id => id !== serviceId) : [...prev, serviceId]);
    };
    const handleApplyAppInsightsServiceSelection = () => { setSelectedServiceIdsForAppInsights(stagedServiceIdsForAppInsights); handleAppInsightsServiceDropdownClose(); };
    const handleAppInsightsServiceSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => setAppInsightsServiceSearchTerm(event.target.value);

    const filteredAvailableClustersForInsights = useMemo(() => selectedClusterNames.filter(name => name.toLowerCase().includes(insightsClusterSearchTerm.toLowerCase())), [selectedClusterNames, insightsClusterSearchTerm]);
    const filteredAvailableClusters = useMemo(() => availableClusterNames.filter(name => name.toLowerCase().includes(clusterSearchTerm.toLowerCase())), [availableClusterNames, clusterSearchTerm]);
    const clustersForHierarchyView = useMemo(() => allClusters.filter(c => selectedClusterNames.includes(c.name)), [allClusters, selectedClusterNames]);

    const handleClusterBoxClick = (clusterId: string) => {
        if (expandedClusterId === clusterId) { setExpandedClusterId(null); setExpandedApplicationId(null); }
        else { setExpandedClusterId(clusterId); setExpandedApplicationId(null); }
    };
    const handleApplicationBoxClick = (applicationId: string) => setExpandedApplicationId(prev => (prev === applicationId ? null : applicationId));

    const datePeriodText = useMemo(() => {
        if (!dateRange.startDate) return "Selected Period";
        switch (activeDateFilter) {
            case 'yesterday': return 'Yesterday';
            case 'last7days': return 'Last 7 Days';
            case 'last30days': return 'Last 30 Days';
            case 'custom':
                if (dateRange.startDate && dateRange.endDate) {
                    if (isEqual(startOfDay(dateRange.startDate), startOfDay(dateRange.endDate))) return format(dateRange.startDate, 'MMM d, yy');
                    return `${format(dateRange.startDate, 'MMM d')} - ${format(dateRange.endDate, 'MMM d, yy')}`;
                } return dateRange.startDate ? `${format(dateRange.startDate, 'MMM d, yy')}...` : "Custom";
            default: return "Selected Period";
        }
    }, [activeDateFilter, dateRange]);

    const HealthDisplayItem: React.FC<{ id: string; name: string; health: HealthStatus; type: 'Cluster' | 'Application' | 'Service'; onClick?: (id: string) => void; isSelected?: boolean; itemRef?: (el: HTMLDivElement | null) => void; }> = ({ id, name, health, type, onClick, isSelected, itemRef }) => {
        const isService = type === 'Service';
        const serviceIsOverallHealthy = health.healthy >= 70; 
        const healthyIconUrl = "/assets/tickcircle.svg"; const unhealthyIconUrl = "/assets/close-circle-fill.svg"; 
        return (
            <Paper ref={itemRef} elevation={0} onClick={() => onClick && onClick(id)} sx={{ p: isService ? '4px 10px' : '4px 10px', borderRadius: '50px', textAlign: 'center', minWidth: isService ? 'auto' : '140px', maxWidth: isService ? '220px' : '220px', height: isService ? '32px' : '52px', display: 'flex', flexDirection: isService ? 'row' : 'column', justifyContent: 'center', alignItems: 'center', bgcolor: isSelected ? '#f0f0f3' : 'white', border: `1px solid ${isSelected ? '#bababa' : '#f0f0f0'}`, boxShadow: isSelected ? '0px 1px 3px rgba(0,0,0,0.1)' : '0px 1px 2px rgba(0,0,0,0.03)', cursor: onClick ? 'pointer' : 'default', transition: 'background-color 0.2s, border-color 0.2s, box-shadow 0.2s', '&:hover': { borderColor: onClick ? (isSelected ? '#a0a0a0' : '#c0c0c0') : (isSelected ? '#bababa' : '#e0e0e0'), boxShadow: onClick ? (isSelected ? '0px 2px 5px rgba(0,0,0,0.12)' : '0px 2px 4px rgba(0,0,0,0.06)') : (isSelected ? '0px 1px 3px rgba(0,0,0,0.1)' : '0px 1px 2px rgba(0,0,0,0.03)'), } }}>
                <Typography variant={isService ? "caption" : "body2"} sx={{ fontFamily: 'Gilroy-Bold', mb: !isService ? 0.2 : 0, mr: isService ? 0.5 : 0, wordBreak: 'break-word', fontWeight: 600, fontSize: isService ? '0.65rem' : '0.75rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', }}>{name}</Typography>
                {!isService ? (<Stack direction="column" spacing={0.1} justifyContent="center" alignItems="center" sx={{ mt: 0.3 }}><Box display="flex" alignItems="center"><Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#12B76A', mr: 0.25 }} /><Typography variant="caption" sx={{ fontFamily: 'Gilroy-Medium', fontSize: '10px', color: 'text.secondary', lineHeight: 1, mr: 1 }}>Healthy: {health.healthy}%</Typography><Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: health.unhealthy >= 0 ? '#FF0000' : '#e0e0e0' , mr: 0.25 }} /><Typography variant="caption" sx={{ fontFamily: 'Gilroy-Medium', fontSize: '10px', color: 'text.secondary', lineHeight: 1 }}>Unhealthy: {health.unhealthy}%</Typography></Box></Stack>)
                    : (<Chip icon={<img src={serviceIsOverallHealthy ? healthyIconUrl : unhealthyIconUrl} alt={serviceIsOverallHealthy ? "Healthy" : "Unhealthy"} style={{ width: '10px', height: '10px' }} />} label={serviceIsOverallHealthy ? 'Healthy' : 'Unhealthy'} size="small" sx={{ height: '18px', fontSize: '0.55rem', fontFamily: 'Gilroy-SemiBold', bgcolor: serviceIsOverallHealthy ? '#12B76A33' : '#F0443833', color: serviceIsOverallHealthy ? '#12B76A' : '#F04438', '.MuiChip-label': { px: '4px', py: '2px', lineHeight: '1' }, '.MuiChip-icon': { ml: '4px', mr: '-2px', fontSize: '0.7rem', display: 'flex', alignItems: 'center' } }} />)}
            </Paper>
        )
    };

    const ConnectorLine: React.FC<{ type: 'vertical' | 'horizontal-branch' | 'horizontal-main'; length?: string; customImageUrl?: string; lineThickness: string; dashColor: string; dashCycleLength: string; dashPercent: string; }> = ({ type, length, customImageUrl, lineThickness, dashColor, dashCycleLength, dashPercent }) => {
        if (customImageUrl) return <Box component="img" src={customImageUrl} alt="connector" sx={{ display: 'block', margin: 'auto' }} />;
        const commonLineStyle: React.CSSProperties = { backgroundColor: 'transparent', margin: '0 auto', };
        if (type === 'vertical' || type === 'horizontal-branch') return <Box sx={{ ...commonLineStyle, width: lineThickness, height: length, backgroundImage: `linear-gradient(to bottom, ${dashColor} 0%, ${dashColor} ${dashPercent}, transparent ${dashPercent}, transparent 100%)`, backgroundRepeat: 'repeat-y', backgroundSize: `100% ${dashCycleLength}`, }} />;
        if (type === 'horizontal-main') return <Box sx={{ ...commonLineStyle, height: lineThickness, width: length || '100%', backgroundImage: `linear-gradient(to right, ${dashColor} 0%, ${dashColor} ${dashPercent}, transparent ${dashPercent}, transparent 100%)`, backgroundRepeat: 'repeat-x', backgroundSize: `${dashCycleLength} 100%`, }} />;
        return null;
    };


    const chartColors = useMemo(() => generateColors(12), []);
    const baseSeedForCharts = useMemo(() => activeDateFilter + (dateRange.startDate?.toISOString() || '') + (dateRange.endDate?.toISOString() || ''), [activeDateFilter, dateRange.startDate, dateRange.endDate]);

    const getLineChartDatasetOptions = useCallback((itemName: string, index: number, seedOffset: number) => ({
        label: itemName, data: generateClusterSpecificTimeData(itemName, baseSeedForCharts, chartTimePoints, 95, 5, index + seedOffset), 
        borderColor: chartColors[index % chartColors.length], fill: false, tension: 0.4, pointRadius: 0, pointHoverRadius: 5, pointHitRadius: 10, borderWidth: 2,
    }), [baseSeedForCharts, chartTimePoints, chartColors]);

    const cpuChartData = useMemo((): ChartData<'line', TimeChartDataPoint[], number> => ({ datasets: selectedClustersForInsights.map((clusterName, index) => getLineChartDatasetOptions(clusterName, index, 0)), }), [selectedClustersForInsights, getLineChartDatasetOptions]);
    
    const memoryChartData = useMemo((): ChartData<'bar', TimeChartDataPoint[], number> => {
        const dailySeriesPerCluster: Record<string, TimeChartDataPoint[]> = {};
        if (chartTimePoints.length > 0 && selectedClustersForInsights.length > 0) {
            selectedClustersForInsights.forEach((clusterName, clusterIndex) => { dailySeriesPerCluster[clusterName] = generateClusterSpecificTimeData(clusterName, baseSeedForCharts, chartTimePoints, 90, 10, clusterIndex + (2 * selectedClustersForInsights.length)); });
        }
        const aggregatedData: TimeChartDataPoint[] = chartTimePoints.map((date, i) => { let totalMemoryForTimePoint = 0; selectedClustersForInsights.forEach((clusterName) => { if (dailySeriesPerCluster[clusterName]?.[i]) totalMemoryForTimePoint += dailySeriesPerCluster[clusterName][i].y; }); return { x: date.getTime(), y: parseFloat(totalMemoryForTimePoint.toFixed(1)) }; });
        return { datasets: [{ label: 'Used', data: aggregatedData, backgroundColor: '#2B7FFF', borderColor: '#2B7FFF', borderWidth: 1, barPercentage: 0.5, categoryPercentage: 0.7, }], };
    }, [chartTimePoints, selectedClustersForInsights, chartColors, baseSeedForCharts]); 

    const apiCallStatsChartData = useMemo(() => {
        if (!selectedApiForTraffic || !apiStatsData) return [];

        const selectedApi = apiStatsData.find(api => api.apiName === selectedApiForTraffic);
        if (!selectedApi) return [];

        const totalSuccess = parseInt(selectedApi.totalSuccess.replace(/,/g, ''), 10);
        const totalFailed = parseInt(selectedApi.totalFailed.replace(/,/g, ''), 10);
        const total = totalSuccess + totalFailed;

        if (total === 0) return []; 

        return [
            { name: 'Success', value: (totalSuccess / total) * 100, rawCount: totalSuccess.toLocaleString() },
            { name: 'Failed', value: (totalFailed / total) * 100, rawCount: totalFailed.toLocaleString() }
        ];
    }, [selectedApiForTraffic, apiStatsData]);

    const apiCallStatsColors = {
        'Success': '#3CD856', 
        'Failed': '#FA5A7D'   
    };

    const buyloadsTrafficChartData = useMemo((): ChartData<'line', TimeChartDataPoint[], number> => {
        if (!selectedApiForTraffic) {
            return { datasets: [] };
        }
        const apiTrafficData = generateClusterSpecificTimeData(
            selectedApiForTraffic,
            baseSeedForCharts,
            chartTimePoints,
            25, 
            5,  
            DUMMY_APIS.indexOf(selectedApiForTraffic) 
        );

        return {
            datasets: [{
                label: 'Requests per Second', 
                data: apiTrafficData,
                borderColor: chartColors[3 % chartColors.length],
                fill: {
                    target: 'origin',
                    above: theme.palette.primary.light + '30', 
                },
                tension: 0.4,
                pointRadius: 0,
                pointHoverRadius: 5,
                pointHitRadius: 10,
                borderWidth: 2,
            }],
        };
    }, [selectedApiForTraffic, baseSeedForCharts, chartTimePoints, chartColors, theme]);

    const buyloadStatsChartData = useMemo((): ChartData<'doughnut'> => {
        const selectedApiData = apiStatsData.find(api => api.apiName === selectedApiForTraffic);

        if (!selectedApiData) {
            return { labels: [], datasets: [] };
        }

        const totalSuccess = parseInt(selectedApiData.totalSuccess.replace(/,/g, ''), 10);
        const totalFailed = parseInt(selectedApiData.totalFailed.replace(/,/g, ''), 10);

        return {
            labels: ['Success', 'Failed'],
            datasets: [{
                label: 'API Call Stats',
                data: [totalSuccess, totalFailed],
                backgroundColor: [
                    '#3CD856', 
                    '#FA5A7D', 
                ],
                borderRadius: 8,
                borderWidth: 3,
                borderColor: '#FFFFFF',
                hoverBorderWidth: 0,
            }],
        };
    }, [selectedApiForTraffic, apiStatsData]); 

    const baseChartPluginsAndLayout = useMemo(() => ({
        plugins: { legend: { display: true, position: 'bottom' as const, align: 'start' as const, labels: { boxWidth: 12, padding: 20, font: { size: 12, family: "Gilroy-SemiBold" }, usePointStyle: true, pointStyle: 'circle' as const, generateLabels: (chart: ChartJS) => { const oL = ChartJS.defaults.plugins.legend.labels.generateLabels(chart); oL.forEach(l => { if (l.strokeStyle) l.fillStyle = l.strokeStyle; else if (l.fillStyle) {/*keep*/} l.pointStyle = 'circle'; }); return oL; } }, onClick: () => { } }, tooltip: { enabled: false, mode: 'index' as const, intersect: false, position: 'nearest' as const } },
        layout: { padding: { top: 10, bottom: 5, left: 5, right: 10 } }, animation: { duration: 0 }
    }), []);

    const lineChartOptions = useMemo((): ChartOptions<'line'> => ({ responsive: true, maintainAspectRatio: false, ...(baseChartPluginsAndLayout as any), plugins: { ...baseChartPluginsAndLayout.plugins, tooltip: { ...baseChartPluginsAndLayout.plugins.tooltip, external: ((c: CustomGrafanaLineTooltipContext) => CustomHtmlLineTooltipGrafana(c as CustomGrafanaLineTooltipContext)) as any } }, interaction: { mode: 'index' as const, intersect: false, }, scales: { x: { type: 'time', min: dateRange.startDate?.getTime(), max: dateRange.endDate?.getTime(), time: { unit: 'day', tooltipFormat: 'PPP', displayFormats: { day: 'MMM d' } }, grid: { display: false }, ticks: { font: { size: 11, family: 'Gilroy-Regular' }, major: { enabled: false }, source: 'auto', autoSkip: false } }, y: { grid: { display: false }, ticks: { font: { size: 11, family: 'Gilroy-Regular' }, padding: 10, callback: function (v) { return typeof v === 'number' ? v + '%' : v; } }, beginAtZero: true, suggestedMax: 100 } } }), [baseChartPluginsAndLayout, dateRange.startDate, dateRange.endDate]);
    const memoryChartOptions = useMemo((): ChartOptions<'bar'> => ({ responsive: true, maintainAspectRatio: false, ...(baseChartPluginsAndLayout as any), plugins: { ...baseChartPluginsAndLayout.plugins, tooltip: { ...baseChartPluginsAndLayout.plugins.tooltip, external: ((c: CustomGrafanaBarTooltipContext) => CustomHtmlBarTooltipGrafana(c as CustomGrafanaBarTooltipContext)) as any }, legend: { ...baseChartPluginsAndLayout.plugins.legend, display: true, labels: { ...baseChartPluginsAndLayout.plugins.legend.labels, generateLabels: (chart: ChartJS) => { const oL = ChartJS.defaults.plugins.legend.labels.generateLabels(chart); oL.forEach(l => { l.pointStyle = 'circle'; if (!l.fillStyle && l.datasetIndex !== undefined && chart.data.datasets[l.datasetIndex]?.backgroundColor) l.fillStyle = chart.data.datasets[l.datasetIndex].backgroundColor as string; }); return oL; } } } }, interaction: { mode: 'index' as const, intersect: false, }, scales: { x: { type: 'time', min: dateRange.startDate?.getTime(), max: dateRange.endDate?.getTime(), time: { unit: 'day', tooltipFormat: 'PPP', displayFormats: { day: 'MMM d' } }, grid: { display: false }, ticks: { font: { size: 11, family: 'Gilroy-Regular' }, major: { enabled: false }, source: 'auto', autoSkip: false } }, y: { grid: { display: false }, ticks: { font: { size: 11, family: 'Gilroy-Regular' }, padding: 10, callback: function (v) { return typeof v === 'number' ? v.toFixed(0) : v; } }, beginAtZero: true } } }), [baseChartPluginsAndLayout, dateRange.startDate, dateRange.endDate]);

    const buyloadsTrafficChartOptions = useMemo((): ChartOptions<'line'> => ({
        ...lineChartOptions, 
        scales: {
            ...lineChartOptions.scales,
            y: {
                ...lineChartOptions.scales?.y,
                suggestedMax: 30, 
                ticks: {
                    font: { size: 11, family: 'Gilroy-Regular' },
                    padding: 10,
                    callback: function (v) { return v; }
                },
            }
        }
    }), [lineChartOptions]);

    const buyloadStatsChartOptions = useMemo((): ChartOptions<'doughnut'> => ({
        responsive: true,
        maintainAspectRatio: false,
        cutout: '40%',
        layout: {
            padding: 5
        },
        plugins: {
            legend: {
                display: true,
                position: 'bottom',
                align: 'center',
                labels: {
                    boxWidth: 12,
                    padding: 20,
                    font: { size: 11, family: "Gilroy-SemiBold" }
                }
            },
            tooltip: {
                enabled: false, 
                external: ((c: CustomGrafanaDoughnutTooltipContext) => CustomHtmlDoughnutTooltipGrafana(c)) as any,
                backgroundColor: 'rgba(255, 255, 255, 0.97)',
                borderColor: '#e0e0e0',
                borderWidth: 1,
                cornerRadius: 6,
                padding: 10,
                titleFont: { family: 'Gilroy-Bold', size: 13 },
                bodyFont: { family: 'Gilroy-Regular', size: 12 },
                titleColor: '#000',
                bodyColor: '#000',
                displayColors: true, 
                boxPadding: 4,
            }
        },
    }), []);

interface CustomGrafanaDoughnutTooltipContext {
    chart: ChartJS<'doughnut', number[], unknown>;
    tooltip: TooltipModel<'doughnut'>;
}

const CustomHtmlDoughnutTooltipGrafana = ({ chart, tooltip }: CustomGrafanaDoughnutTooltipContext): void => {
    let tooltipEl = chart.canvas.parentNode?.querySelector<HTMLDivElement>('div.chartjs-custom-tooltip-grafana-doughnut');
    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.className = 'chartjs-custom-tooltip-grafana-doughnut';
        Object.assign(tooltipEl.style, {
            background: 'rgba(255, 255, 255, 0.97)', borderRadius: '6px', color: 'black', opacity: '0',
            pointerEvents: 'none', position: 'absolute', transition: 'opacity 0.15s ease, top 0.15s ease, left 0.15s ease',
            boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.18)', border: '1px solid #e0e0e0', minWidth: '180px', zIndex: '1001'
        });
        const rootDiv = document.createElement('div');
        tooltipEl.appendChild(rootDiv);
        chart.canvas.parentNode?.appendChild(tooltipEl);
    }

    if (tooltip.opacity === 0) {
        if (tooltipEl.style.opacity !== '0') {
            tooltipEl.style.opacity = '0';
            const contentRoot = tooltipEl.querySelector('div');
            if (contentRoot && contentRoot.hasChildNodes()) ReactDOM.unmountComponentAtNode(contentRoot);
        }
        return;
    }

    const dataPoint = tooltip.dataPoints?.[0];
    if (!dataPoint) return;

    const title = dataPoint.label || '';
    const value = dataPoint.raw as number;
    const total = (dataPoint.dataset.data as number[]).reduce((a, b) => a + b, 0);
    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) + '%' : '0.0%';
    const formattedValue = `${value.toLocaleString()} (${percentage})`;
    const label = dataPoint.dataset.label || 'Count';
    const color = (dataPoint.dataset.backgroundColor as string[])[dataPoint.dataIndex] || '#000';

    const contentRoot = tooltipEl.querySelector('div');
    if (!contentRoot) return;

    ReactDOM.render(
        <Box sx={{ p: 1.5, fontFamily: 'Gilroy-Regular', maxWidth: '300px' }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', fontFamily: 'Gilroy-Bold', textAlign: 'left' }}>{title}</Typography>
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: color, borderRadius: '2px', mr: 1, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ mr: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'Gilroy-Regular' }} title={label}>{label}</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 'medium', flexShrink: 0, pl: 1, fontFamily: 'Gilroy-SemiBold' }}>{formattedValue}</Typography>
            </Box>
        </Box>,
        contentRoot
    );

    const { offsetLeft: canvasOffsetX, offsetTop: canvasOffsetY } = chart.canvas;
    const chartArea = chart.chartArea;
    if (!chartArea) return;
    const caretX = tooltip.caretX, caretY = tooltip.caretY;
    const tooltipWidth = tooltipEl.offsetWidth, tooltipHeight = tooltipEl.offsetHeight;
    const tooltipPadding = 12;
    let finalLeft = canvasOffsetX + caretX + tooltipPadding, finalTop = canvasOffsetY + caretY - (tooltipHeight / 2);

    if (finalLeft + tooltipWidth + tooltipPadding > canvasOffsetX + chartArea.right) finalLeft = canvasOffsetX + caretX - tooltipWidth - tooltipPadding;
    if (finalLeft < canvasOffsetX + chartArea.left + tooltipPadding) finalLeft = canvasOffsetX + chartArea.left + tooltipPadding;
    if (finalTop < canvasOffsetY + chartArea.top + tooltipPadding) finalTop = canvasOffsetY + chartArea.top + tooltipPadding;
    if (finalTop + tooltipHeight + tooltipPadding > canvasOffsetY + chartArea.bottom) finalTop = canvasOffsetY + chartArea.bottom - tooltipHeight - tooltipPadding;

    tooltipEl.style.opacity = '1';
    tooltipEl.style.left = finalLeft + 'px';
    tooltipEl.style.top = finalTop + 'px';
};

interface CustomCategoricalBarTooltipContext {
    chart: ChartJS<'bar', number[], unknown>;
    tooltip: TooltipModel<'bar'>;
    valueSuffix?: string;
}

const CustomHtmlCategoricalBarTooltipGrafana = ({ chart, tooltip, valueSuffix = 's' }: CustomCategoricalBarTooltipContext): void => {
    let tooltipEl = chart.canvas.parentNode?.querySelector<HTMLDivElement>('div.chartjs-custom-tooltip-categorical-bar');
    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.className = 'chartjs-custom-tooltip-categorical-bar';
        Object.assign(tooltipEl.style, {
            background: 'rgba(255, 255, 255, 0.97)', borderRadius: '6px', color: 'black', opacity: '0',
            pointerEvents: 'none', position: 'absolute', transition: 'opacity 0.15s ease, top 0.15s ease, left 0.15s ease',
            boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.18)', border: '1px solid #e0e0e0', minWidth: '180px', zIndex: '1001'
        });
        const rootDiv = document.createElement('div');
        tooltipEl.appendChild(rootDiv);
        chart.canvas.parentNode?.appendChild(tooltipEl);
    }

    if (tooltip.opacity === 0) {
        if (tooltipEl.style.opacity !== '0') {
            tooltipEl.style.opacity = '0';
            const contentRoot = tooltipEl.querySelector('div');
            if (contentRoot && contentRoot.hasChildNodes()) ReactDOM.unmountComponentAtNode(contentRoot);
        }
        return;
    }

    const dataPoint = tooltip.dataPoints?.[0];
    if (!dataPoint) return;

    const title = tooltip.title?.[0] || dataPoint.label || '';
    const value = dataPoint.parsed?.y;
    const formattedValue = typeof value === 'number' ? `${value.toFixed(2)}${valueSuffix}` : '';
    const label = dataPoint.dataset?.label || 'Value';
    
    let color = '#000';
    const bgColors = dataPoint.dataset?.backgroundColor;
    if (Array.isArray(bgColors)) {
        color = bgColors[dataPoint.dataIndex] as string;
    } else if (typeof bgColors === 'string') {
        color = bgColors;
    }

    const contentRoot = tooltipEl.querySelector('div');
    if (!contentRoot) return;

    ReactDOM.render(
        <Box sx={{ p: 1.5, fontFamily: 'Gilroy-Regular', maxWidth: '300px' }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', fontFamily: 'Gilroy-Bold', textAlign: 'left' }}>{title}</Typography>
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: color, borderRadius: '2px', mr: 1, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ mr: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'Gilroy-Regular' }} title={label}>{label}</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 'medium', flexShrink: 0, pl: 1, fontFamily: 'Gilroy-SemiBold' }}>{formattedValue}</Typography>
            </Box>
        </Box>,
        contentRoot
    );

    const { offsetLeft: canvasOffsetX, offsetTop: canvasOffsetY } = chart.canvas;
    const chartArea = chart.chartArea;
    if (!chartArea) return;
    const caretX = tooltip.caretX, caretY = tooltip.caretY;
    const tooltipWidth = tooltipEl.offsetWidth, tooltipHeight = tooltipEl.offsetHeight;
    const tooltipPadding = 12;
    let finalLeft = canvasOffsetX + caretX + tooltipPadding, finalTop = canvasOffsetY + caretY - (tooltipHeight / 2);

    if (finalLeft + tooltipWidth + tooltipPadding > canvasOffsetX + chartArea.right) finalLeft = canvasOffsetX + caretX - tooltipWidth - tooltipPadding;
    if (finalLeft < canvasOffsetX + chartArea.left + tooltipPadding) finalLeft = canvasOffsetX + chartArea.left + tooltipPadding;
    if (finalTop < canvasOffsetY + chartArea.top + tooltipPadding) finalTop = canvasOffsetY + chartArea.top + tooltipPadding;
    if (finalTop + tooltipHeight + tooltipPadding > canvasOffsetY + chartArea.bottom) finalTop = canvasOffsetY + chartArea.bottom - tooltipHeight - tooltipPadding;

    tooltipEl.style.opacity = '1';
    tooltipEl.style.left = finalLeft + 'px';
    tooltipEl.style.top = finalTop + 'px';
};


    const wafBotData = useMemo(() => {
        const nonBotsData = generateClusterSpecificTimeData(
            'Non-Bots', baseSeedForCharts, chartTimePoints, 4500, 3500, 50
        ).map(d => ({ ...d, y: Math.round(d.y) }));

        const unverifiedBotsData = generateClusterSpecificTimeData(
            'Unverified Bots', baseSeedForCharts, chartTimePoints, 400, 200, 51
        ).map(d => ({ ...d, y: Math.round(d.y) }));

        const verifiedBotsData = generateClusterSpecificTimeData(
            'Verified Bots', baseSeedForCharts, chartTimePoints, 100, 40, 52
        ).map(d => ({ ...d, y: Math.round(d.y) }));

        return {
            'Non-Bots': nonBotsData,
            'Unverified Bots': unverifiedBotsData,
            'Verified Bots': verifiedBotsData,
        };
    }, [baseSeedForCharts, chartTimePoints]);

    const wafBotColors = {
        'Non-Bots': '#4DD0E1',      
        'Unverified Bots': '#FFB74D', 
        'Verified Bots': '#81C784',  
    };

    const wafBotLineChartData = useMemo((): ChartData<'line', TimeChartDataPoint[], number> => {
        return {
            datasets: [
                {
                    label: 'Non-Bots',
                    data: wafBotData['Non-Bots'],
                    borderColor: wafBotColors['Non-Bots'],
                    fill: false, tension: 0.4, pointRadius: 0, pointHoverRadius: 5, pointHitRadius: 10, borderWidth: 2,
                },
                {
                    label: 'Unverified Bots',
                    data: wafBotData['Unverified Bots'],
                    borderColor: wafBotColors['Unverified Bots'],
                    fill: false, tension: 0.4, pointRadius: 0, pointHoverRadius: 5, pointHitRadius: 10, borderWidth: 2,
                },
                {
                    label: 'Verified Bots',
                    data: wafBotData['Verified Bots'],
                    borderColor: wafBotColors['Verified Bots'],
                    fill: false, tension: 0.4, pointRadius: 0, pointHoverRadius: 5, pointHitRadius: 10, borderWidth: 2,
                },
            ],
        };
    }, [wafBotData]);

    const wafBotDoughnutChartData = useMemo((): ChartData<'doughnut'> => {
        const totalNonBots = wafBotData['Non-Bots'].reduce((sum, point) => sum + point.y, 0);
        const totalUnverified = wafBotData['Unverified Bots'].reduce((sum, point) => sum + point.y, 0);
        const totalVerified = wafBotData['Verified Bots'].reduce((sum, point) => sum + point.y, 0);

        return {
            labels: ['Non-Bots', 'Unverified Bots', 'Verified Bots'],
            datasets: [{
                label: 'WAF Bot Distribution',
                data: [totalNonBots, totalUnverified, totalVerified],
                backgroundColor: [
                    wafBotColors['Non-Bots'],
                    wafBotColors['Unverified Bots'],
                    wafBotColors['Verified Bots'],
                ],
                borderRadius: 8,
                borderWidth: 3,
                borderColor: '#FFFFFF',
                hoverBorderWidth: 0,
            }],
        };
    }, [wafBotData]);


    const wafBotLineChartOptions = useMemo((): ChartOptions<'line'> => ({
        ...lineChartOptions, 
        plugins: {
            ...lineChartOptions.plugins, 
            tooltip: {
                ...(lineChartOptions.plugins as any).tooltip, 
                enabled: false, 
                external: ((context: CustomGrafanaLineTooltipContext) => CustomHtmlLineTooltipGrafana({ ...context, valueSuffix: '' })) as any,
            },
        },
        scales: {
            ...lineChartOptions.scales,
            y: {
                ...lineChartOptions.scales?.y,
                suggestedMax: undefined, 
                ticks: {
                    ...lineChartOptions.scales?.y?.ticks,
                    callback: function (v) { return v; }
                },
            }
        }
    }), [lineChartOptions]);

    const wafBotDoughnutChartOptions = useMemo((): ChartOptions<'doughnut'> => ({
        ...buyloadStatsChartOptions, 
        cutout: '50%',
        plugins: {
            ...buyloadStatsChartOptions.plugins,
            tooltip: {
                ...buyloadStatsChartOptions.plugins?.tooltip,
                callbacks: {
                    label: function(context) {
                        const label = context.label || '';
                        const rawValue = context.raw as number;
                        const allData = context.chart.data.datasets[0].data;
                        const total = allData.reduce((acc: number, val) => acc + (typeof val === 'number' ? val : 0), 0);
                        const percentage = total > 0 ? ((rawValue / total) * 100).toFixed(1) : '0.0';
                        return ` ${label}: ${percentage}%`;
                    }
                },
            }
        }
    }), [buyloadStatsChartOptions]);


const wafRequestData = useMemo(() => {
    const allowedRequests = generateClusterSpecificTimeData(
        'Allowed Requests', baseSeedForCharts, chartTimePoints, 4800, 3800, 60
    ).map(d => ({ ...d, y: Math.round(d.y) }));

    const countedRequests = generateClusterSpecificTimeData(
        'Counted Requests', baseSeedForCharts, chartTimePoints, 1000, 500, 61
    ).map(d => ({ ...d, y: Math.round(d.y) }));

    return {
        'Allowed Requests': allowedRequests,
        'Counted Requests': countedRequests,
    };
}, [baseSeedForCharts, chartTimePoints]);


const allowedRequestsChartData = useMemo((): ChartData<'line', TimeChartDataPoint[], number> => ({
    datasets: [{
        label: 'Allowed',
        data: wafRequestData['Allowed Requests'],
        borderColor: '#6495ED', 
        fill: false, tension: 0.4, pointRadius: 0, pointHoverRadius: 5, pointHitRadius: 10, borderWidth: 2,
    }],
}), [wafRequestData]);

const countedRequestsChartData = useMemo((): ChartData<'line', TimeChartDataPoint[], number> => ({
    datasets: [{
        label: 'Counted',
        data: wafRequestData['Counted Requests'],
        borderColor: '#FFB74D', 
        fill: false, tension: 0.4, pointRadius: 0, pointHoverRadius: 5, pointHitRadius: 10, borderWidth: 2,
    }],
}), [wafRequestData]);

const wafRequestChartOptions = useMemo((): ChartOptions<'line'> => ({
    ...wafBotLineChartOptions, 
}), [wafBotLineChartOptions]);

    // ----------------------------------------------------------------------------------
    // NEW: LLM Observability Metrics Mock Data & Charts
    // ----------------------------------------------------------------------------------
    
    const llmAgents = ['Support Agent', 'Analytics Agent'];
    const llmColors = ['#81C784', '#BA68C8']; 

    const llmUsageData = useMemo(() => {
        const agentA = generateClusterSpecificTimeData('Support Agent', baseSeedForCharts, chartTimePoints, 15000, 5000, 70).map(d => ({ ...d, y: Math.round(d.y) }));
        const agentB = generateClusterSpecificTimeData('Analytics Agent', baseSeedForCharts, chartTimePoints, 8000, 2000, 71).map(d => ({ ...d, y: Math.round(d.y) }));
        return { 'Support Agent': agentA, 'Analytics Agent': agentB };
    }, [baseSeedForCharts, chartTimePoints]);

    const llmUsageLineChartData = useMemo((): ChartData<'line', TimeChartDataPoint[], number> => ({
        datasets: [
            { 
                label: 'Support Agent', 
                data: llmUsageData['Support Agent'], 
                borderColor: llmColors[0], 
                fill: { target: 'origin', above: llmColors[0] + '30' },
                tension: 0.4, 
                borderWidth: 2, 
                pointRadius: 0, 
                pointHoverRadius: 5 
            },
            { 
                label: 'Analytics Agent', 
                data: llmUsageData['Analytics Agent'], 
                borderColor: llmColors[1], 
                fill: { target: 'origin', above: llmColors[1] + '30' },
                tension: 0.4, 
                borderWidth: 2, 
                pointRadius: 0, 
                pointHoverRadius: 5 
            },
        ]
    }), [llmUsageData]);

    const llmUsageChartOptions = useMemo((): ChartOptions<'line'> => ({
        ...buyloadsTrafficChartOptions,
        plugins: {
            ...buyloadsTrafficChartOptions.plugins,
            tooltip: {
                ...(buyloadsTrafficChartOptions.plugins as any).tooltip,
                external: ((context: CustomGrafanaLineTooltipContext) => CustomHtmlLineTooltipGrafana({ ...context, valueSuffix: ' tokens' })) as any,
            }
        },
        scales: {
            ...buyloadsTrafficChartOptions.scales,
            y: {
                ...buyloadsTrafficChartOptions.scales?.y,
                suggestedMax: 20000,
                ticks: {
                    font: { size: 11, family: 'Gilroy-Regular' },
                    padding: 10,
                    callback: function (v) { return v; }
                }
            }
        }
    }), [buyloadsTrafficChartOptions]);

    const llmPerformanceBarData = useMemo((): ChartData<'bar'> => {
        return {
            labels: llmAgents,
            datasets: [{
                label: 'Avg Completion Time (s)',
                data: [1.2, 3.5], 
                backgroundColor: llmColors,
                borderRadius: 4,
            }]
        }
    }, []);

    const llmPerformanceBarOptions = useMemo((): ChartOptions<'bar'> => ({
        responsive: true,
        maintainAspectRatio: false,
        ...(baseChartPluginsAndLayout as any),
        plugins: {
            ...baseChartPluginsAndLayout.plugins,
            legend: { display: false },
            tooltip: {
                ...baseChartPluginsAndLayout.plugins.tooltip,
                external: ((c: any) => CustomHtmlCategoricalBarTooltipGrafana({...c, valueSuffix: 's'})) as any
            }
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { font: { family: 'Gilroy-Regular', size: 11 } }
            },
            y: {
                grid: { display: true, color: '#f0f0f0' },
                ticks: { font: { family: 'Gilroy-Regular', size: 11 } },
                beginAtZero: true
            }
        }
    }), [baseChartPluginsAndLayout]);

    const llmSplitDoughnutData = useMemo((): ChartData<'doughnut'> => {
         const totalA = llmUsageData['Support Agent'].reduce((sum, p) => sum + p.y, 0);
         const totalB = llmUsageData['Analytics Agent'].reduce((sum, p) => sum + p.y, 0);
         return {
             labels: llmAgents,
             datasets: [{
                 data: [totalA, totalB],
                 backgroundColor: llmColors,
                 borderWidth: 4,
                 hoverBorderWidth: 0,
                 borderRadius: 7
             }]
         }
    }, [llmUsageData]);

    const llmSummaryStats = [
        { label: ['Avg.', 'tokens per message'], value: '342', change: '5.2%', isPositive: false, icon: <ReceiptLongIcon /> },
        { label: ['Avg.', 'cost per message'], value: '$0.00124', change: '1.1%', isPositive: false, icon: <TrendingDownIcon /> },
        { label: ['90th', 'percentile time to first token'], value: '450ms', change: '12%', isPositive: false, icon: <AccessTimeIcon /> },
        { label: ['90th', 'percentile completion time'], value: '4.2s', change: '3.4%', isPositive: true, icon: <AccessTimeIcon /> },
    ];


    return (
        <Container disableGutters maxWidth={false} sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f9fafb", minHeight: "100vh", overflowX: 'hidden' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1}}>
                <Box display="flex" justifyContent="space-between">
                <Box display="flex" alignItems="center"  flexDirection="row">
                    <Typography fontFamily="Gilroy-Bold" fontSize="22px"> Observability Dashboard </Typography>
                </Box>
                <Box>
                 <IconButton
                    aria-label="Help" 
                    onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
                    sx={{
                    padding: 0, 
                    border: 'none',  
                    ml:2,
                    '&:hover': {
                        backgroundColor: 'action.hover', 
                    },
                    }}
                >
                    <Box
                    component="img"
                    src="/assets/Help.svg" 
                    alt="" 
                    sx={{
                        width: 40,  
                        height: 40,
                        display: 'block', 
                    }}
                    />
                </IconButton>
                <Link href={(!isLoadingUrl && grafanaRepoUrl && grafanaRepoUrl !== "Loading..." && !urlFetchError) ? 'http://' + grafanaRepoUrl : undefined} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'none', color: 'inherit', pointerEvents: (!isLoadingUrl && grafanaRepoUrl && grafanaRepoUrl !== "Loading..." && !urlFetchError) ? 'auto' : 'none', }}>
                    <Button sx={{ backgroundColor: "black", fontFamily: 'Gilroy-Bold', color: "white", fontSize: '14px', borderRadius: '6px', textTransform: "none", py: 1, px: 3.5, mr: { xs: 0, md: 2 }, ml: { xs: 0, md: 2 }, '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)', } }} variant="contained" size="large" disabled={isLoadingUrl || !!urlFetchError || !grafanaRepoUrl || grafanaRepoUrl === "Loading..."}>
                        Grafana Dashboard <img src="/assets/external-link-line.svg" alt="external link" style={{ marginLeft: '10px' }} />
                    </Button>
                </Link>
                </Box>
                </Box>
                
            </Box>
            <Divider sx={{ marginY: 1 }} />
            <Box sx={{ display: 'inline-flex', alignItems: 'stretch', height: '38px', bgcolor: 'transparent', borderRadius: '6px', border: '1px solid #dee2e6', overflow: 'hidden', mb: 3 }}>
                <Stack direction="row" spacing={0} alignItems="stretch" height="100%">
                    <Button sx={activeDateFilter === 'yesterday' ? dateFilterActiveStyle : dateFilterInactiveStyle} onClick={() => handleDateFilterClick('yesterday', 'yesterday')} variant="text">Yesterday</Button>
                    <Divider orientation="vertical" flexItem sx={{ bgcolor: '#dee2e6' }} />
                    <Button sx={activeDateFilter === 'last7days' ? dateFilterActiveStyle : dateFilterInactiveStyle} onClick={() => handleDateFilterClick('last7days', 7)} variant="text">Last 7 days</Button>
                    <Divider orientation="vertical" flexItem sx={{ bgcolor: '#dee2e6' }} />
                    <Button sx={activeDateFilter === 'last30days' ? dateFilterActiveStyle : dateFilterInactiveStyle} onClick={() => handleDateFilterClick('last30days', 30)} variant="text">Last 30 Days</Button>
                    <Divider orientation="vertical" flexItem sx={{ bgcolor: '#dee2e6' }} />
                    <DatePicker selectsRange startDate={dateRange.startDate} endDate={dateRange.endDate} onChange={handleCustomDateChange} customInput={<CustomDatePickerInput isActive={activeDateFilter === 'custom'} value={activeDateFilter === 'custom' && dateRange.startDate ? (dateRange.endDate && !isEqual(startOfDay(dateRange.startDate), startOfDay(dateRange.endDate)) ? `${format(dateRange.startDate, 'MMM d, yy')} - ${format(dateRange.endDate, 'MMM d, yy')}` : `${format(dateRange.startDate, 'MMM d, yy')}`) : undefined} />} popperPlacement="bottom-start" popperProps={{ strategy: 'fixed' }} />
                </Stack>
            </Box>

            <Grid container spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                <Grid item xs={12} md={7} lg={8}><Typography variant="h6" sx={{ fontFamily: "Gilroy-Bold" }}>Resource Health Overview</Typography><Typography variant="body2" color="text.secondary" sx={{ fontFamily: "Gilroy-Regular" }}>Showing for {datePeriodText}</Typography></Grid>
                <Grid item xs={12} md={5} lg={4} container justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                    <FormControl sx={{ minWidth: 220, width: '100%', maxWidth: { md: 300 } }} size="small">
                        <Select multiple displayEmpty value={selectedClusterNames} onOpen={handleClusterDropdownOpen} onClose={handleClusterDropdownClose} open={isClusterDropdownOpen} input={<OutlinedInput notched={false} />} IconComponent={KeyboardArrowDownIcon} sx={{ ...generalOutlinedButtonStyle, width: '100%', '& .MuiSelect-select': { py: '7.5px', px: 1.5, textAlign: 'left', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', display: 'block' }, '& .MuiSvgIcon-root': { color: 'black', right: 12 } }}
                            renderValue={(selected) => { if (selected.length === 0) return <Typography sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', color: 'grey.700' }}>Select Clusters</Typography>; const dC = 1; const dN = selected.slice(0, dC).join(', '); let dT = `Clusters: ${dN}`; if (selected.length > dC) dT += `, +${selected.length - dC}`; return <Typography noWrap title={selected.join(', ')} sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', color: 'black' }}>{dT}</Typography>; }}
                            MenuProps={{ autoFocus: false, PaperProps: { sx: { width: { xs: '90%', sm: 300 }, maxHeight: 400 } } }}>
                            <ListSubheader sx={{ bgcolor: 'background.paper', p: 1, pt: 1.5, top: -8, borderBottom: '1px solid #eee' }}>
                                <TextField size="small" fullWidth placeholder="Search clusters (max 6)" value={clusterSearchTerm} onChange={handleClusterSearchChange} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} InputProps={{ startAdornment: (<InputAdornment position="start" sx={{ pl: 0, pr: 1, mt: '0 !important' }}><SearchIcon fontSize="small" /></InputAdornment>), endAdornment: clusterSearchTerm ? (<InputAdornment position="end"><IconButton size="small" onClick={(e) => { e.stopPropagation(); setClusterSearchTerm(''); }}><CloseIcon fontSize="small" /></IconButton></InputAdornment>) : null, sx: { fontSize: '0.875rem', borderRadius: '6px', fontFamily: 'Gilroy-Regular', pl: 1 } }} variant="outlined" autoFocus />
                            </ListSubheader>
                            <Box sx={{ maxHeight: 250, overflow: 'auto', pl: 0.5, pr: 0.5, pt: 0.5 }}>
                                {filteredAvailableClusters.map((name: string) => (<MenuItem key={name} value={name} onClick={() => handleStagedClusterSelectionChange(name)} sx={{ py: 0.3, borderRadius: '4px', m: 0.5, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }} disabled={stagedClusterSelection.length >= 6 && !stagedClusterSelection.includes(name)}><Checkbox checked={stagedClusterSelection.includes(name)} size="small" sx={{ p: 0.5, mr: 1, '&.Mui-checked': { color: 'black' } }} /><ListItemText primary={name} primaryTypographyProps={{ style: { fontSize: '0.875rem', fontFamily: 'Gilroy-Regular' } }} /></MenuItem>))}
                                {filteredAvailableClusters.length === 0 && clusterSearchTerm && (<MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}><Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>No matches found</Typography></MenuItem>)}
                                {availableClusterNames.length === 0 && !clusterSearchTerm && (<MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}><Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>No clusters available</Typography></MenuItem>)}
                            </Box>
                            <Box sx={{ position: 'sticky', bottom: 0, left: 0, right: 0, zIndex: 1, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider', p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', }}><Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '11px' }}>{stagedClusterSelection.length} / 6 Selected</Typography><Button size="small" variant="contained" onClick={handleApplyClusterSelection} sx={{ ...generalContainedButtonStyle, height: 'auto', textTransform: 'uppercase', fontSize: '0.70rem', px: 1.5, py: 0.7 }}>Apply</Button></Box>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            <Paper ref={hierarchyContainerRef} elevation={0} sx={{ p: { xs: 1, md: 1.5 }, borderRadius: "12px", border: '1px solid #e0e0e0', backgroundImage: `repeating-linear-gradient(90deg, transparent, transparent 19px, #f0f0f0 19px, #f0f0f0 20px)`, backgroundSize: '20px 20px', backgroundColor: '#fdfdfd', maxHeight: 'calc(70vh - 100px)', minHeight: '35vh', overflow: 'auto', display: 'flex', justifyContent: 'center', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none', msOverflowStyle: 'none', mb: 3, }}>
                {loadingData ? (<Stack alignItems="center" justifyContent="center" sx={{ py: 5, minHeight: '200px', width: '100%' }}><CircularProgress /><Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontFamily: "Gilroy-Regular" }}>Loading health data...</Typography></Stack>)
                    : errorLoading ? (<Stack alignItems="center" justifyContent="center" sx={{ py: 5, minHeight: '200px', width: '100%' }}><Alert severity="error" sx={{ width: 'auto', fontFamily: "Gilroy-Regular" }}>{errorLoading}</Alert></Stack>)
                        : clustersForHierarchyView.length === 0 ? (<Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 5, fontFamily: "Gilroy-Regular", minHeight: '200px', width: '100%' }}>Please select clusters to view their health overview.</Typography>)
                            : (<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', flexWrap: 'nowrap', gap: { xs: 1, md: 2 }, mt: 0.5, p: 1.5, minWidth: 'fit-content' }}>{clustersForHierarchyView.map((cluster) => (<Stack key={cluster.id} direction="column" alignItems="center" spacing={0} sx={{ position: 'relative', flexShrink: 0, p: 0.5, minWidth: '170px' }}><HealthDisplayItem id={cluster.id} name={cluster.name} health={cluster.health} type="Cluster" onClick={handleClusterBoxClick} isSelected={expandedClusterId === cluster.id} itemRef={(el) => itemRefs.current[cluster.id] = el} />
                                {expandedClusterId === cluster.id && cluster.applications.length > 0 && (<><ConnectorLine type="vertical" length={verticalConnectorLength} lineThickness={lineThickness} dashColor={dashColor} dashCycleLength={dashCycleLength} dashPercent={dashPercent} /><Stack direction="row" spacing={1.5} justifyContent="center" sx={{ position: 'relative', alignItems: 'flex-start', flexWrap: 'wrap', p: '0 2px', width: 'fit-content' }}>
                                    {cluster.applications.length > 1 && (<Box sx={{ position: 'absolute', top: '0px', left: '50%', transform: 'translateX(-50%)', width: '100%', height: lineThickness }}><ConnectorLine type="horizontal-main" length="100%" lineThickness={lineThickness} dashColor={dashColor} dashCycleLength={dashCycleLength} dashPercent={dashPercent} /></Box>)}
                                    {cluster.applications.map((app) => (<Stack key={app.id} direction="column" alignItems="center" spacing={0} sx={{ position: 'relative', mb: 0.5, flexShrink: 0 }}>{cluster.applications.length > 1 && <ConnectorLine type="horizontal-branch" length={branchConnectorLength} lineThickness={lineThickness} dashColor={dashColor} dashCycleLength={dashCycleLength} dashPercent={dashPercent} />}<HealthDisplayItem id={app.id} name={app.name} health={app.health} type="Application" onClick={handleApplicationBoxClick} isSelected={expandedApplicationId === app.id} itemRef={(el) => itemRefs.current[app.id] = el} />
                                        {expandedApplicationId === app.id && app.services.length > 0 && (<><ConnectorLine type="vertical" length={verticalConnectorLength} lineThickness={lineThickness} dashColor={dashColor} dashCycleLength={dashCycleLength} dashPercent={dashPercent} /><Stack direction="row" spacing={1} justifyContent="center" sx={{ position: 'relative', alignItems: 'flex-start', flexWrap: 'wrap', p: '0 1px', width: 'fit-content' }}>
                                            {app.services.length > 1 && (<Box sx={{ position: 'absolute', top: '0px', left: '50%', transform: 'translateX(-50%)', width: '100%', height: lineThickness }}><ConnectorLine type="horizontal-main" length="100%" lineThickness={lineThickness} dashColor={dashColor} dashCycleLength={dashCycleLength} dashPercent={dashPercent} /></Box>)}
                                            {app.services.map((service) => (<Stack key={service.id} direction="column" alignItems="center" spacing={0} sx={{ position: 'relative', flexShrink: 0 }}>{app.services.length > 1 && <ConnectorLine type="horizontal-branch" length={branchConnectorLength} lineThickness={lineThickness} dashColor={dashColor} dashCycleLength={dashCycleLength} dashPercent={dashPercent} />}<HealthDisplayItem id={service.id} name={service.name} health={service.health} type="Service" /></Stack>))}</Stack></>)}</Stack>))}</Stack></>)}</Stack>))}</Box>)}
            </Paper>

            <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "12px", border: '1px solid #e0e0e0', backgroundColor: 'white', mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
                    <Box><Typography variant="h6" sx={{ fontFamily: "Gilroy-Bold" }}>Cluster Insights</Typography><Typography variant="body2" color="text.secondary" sx={{ fontFamily: "Gilroy-Regular", fontSize: '0.875rem' }}>Showing for {datePeriodText}</Typography></Box>
                    <FormControl sx={{ minWidth: 220, maxWidth: { xs: '100%', sm: 300 } }} size="small">
                        <Select multiple displayEmpty value={selectedClustersForInsights} onOpen={handleInsightsClusterDropdownOpen} onClose={handleInsightsClusterDropdownClose} open={isInsightsClusterDropdownOpen} input={<OutlinedInput notched={false} />} IconComponent={KeyboardArrowDownIcon} sx={{ ...generalOutlinedButtonStyle, width: '100%', height: '40px', '& .MuiSelect-select': { py: '8.5px', px: 1.5 }, '& .MuiSvgIcon-root': { color: 'black', right: 12 } }}
                            renderValue={(selected) => { if (selectedClusterNames.length === 0) return <Typography sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', color: 'grey.700' }}>No clusters in overview</Typography>; if (selected.length === 0) return <Typography sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', color: 'grey.700' }}>Select Clusters for Charts</Typography>; if (selected.length === selectedClusterNames.length && selected.length > 0) return <Typography sx={{ fontFamily: 'Gilroy-Bold', fontSize: '14px' }}>All Displayed ({selected.length})</Typography>; const dC = 1; const dN = selected.slice(0, dC).join(', '); let dT = `${dN}`; if (selected.length > dC) dT += `, ... (+${selected.length - dC})`; return <Typography noWrap title={selected.join(', ')} sx={{ fontFamily: 'Gilroy-Bold', fontSize: '14px' }}>{dT}</Typography>; }}
                            MenuProps={{ autoFocus: false, PaperProps: { sx: { width: { xs: '90%', sm: 300 }, maxHeight: 400 } } }}>
                            <ListSubheader sx={{ bgcolor: 'background.paper', p: 1, pt: 1.5, top: -8, borderBottom: '1px solid #eee' }}><TextField size="small" fullWidth placeholder="Search from selected clusters" value={insightsClusterSearchTerm} onChange={handleInsightsClusterSearchChange} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} InputProps={{ startAdornment: (<InputAdornment position="start" sx={{ pl: 0, pr: 1, mt: '0 !important' }}><SearchIcon fontSize="small" /></InputAdornment>), endAdornment: insightsClusterSearchTerm ? (<InputAdornment position="end"><IconButton size="small" onClick={(e) => { e.stopPropagation(); setInsightsClusterSearchTerm(''); }}><CloseIcon fontSize="small" /></IconButton></InputAdornment>) : null, sx: { fontSize: '0.875rem', borderRadius: '6px', fontFamily: 'Gilroy-Regular', pl: 1 } }} variant="outlined" autoFocus /></ListSubheader>
                            <Box sx={{ maxHeight: 250, overflow: 'auto', pl: 0.5, pr: 0.5, pt: 0.5 }}>
                                {filteredAvailableClustersForInsights.map((name: string) => (<MenuItem key={name} value={name} onClick={() => handleStagedInsightsClusterSelectionChange(name)} sx={{ py: 0.3, borderRadius: '4px', m: 0.5, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}><Checkbox checked={stagedClustersForInsights.includes(name)} size="small" sx={{ p: 0.5, mr: 1, '&.Mui-checked': { color: 'black' } }} /><ListItemText primary={name} primaryTypographyProps={{ style: { fontSize: '0.875rem', fontFamily: 'Gilroy-Regular' } }} /></MenuItem>))}
                                {filteredAvailableClustersForInsights.length === 0 && insightsClusterSearchTerm && (<MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}><Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>No matches found</Typography></MenuItem>)}
                                {selectedClusterNames.length === 0 && (<MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}><Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>No clusters selected in overview</Typography></MenuItem>)}
                            </Box>
                            <Box sx={{ position: 'sticky', bottom: 0, left: 0, right: 0, zIndex: 1, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider', p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', }}><Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '11px' }}>{stagedClustersForInsights.length} / {selectedClusterNames.length} Selected</Typography><Button size="small" variant="contained" onClick={handleApplyInsightsClusterSelection} sx={{ ...generalContainedButtonStyle, height: 'auto', textTransform: 'uppercase', fontSize: '0.70rem', px: 1.5, py: 0.7 }}>Apply</Button></Box>
                        </Select>
                    </FormControl>
                </Box>
                {loadingData && (<Stack alignItems="center" justifyContent="center" sx={{ minHeight: '300px' }}><CircularProgress /><Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Loading chart data...</Typography></Stack>)}
                {!loadingData && errorLoading && (<Alert severity="error">Failed to load data: {errorLoading}</Alert>)}
                {!loadingData && !errorLoading && selectedClusterNames.length > 0 && selectedClustersForInsights.length === 0 && (<Typography sx={{ textAlign: 'center', py: 5, fontFamily: 'Gilroy-Regular', color: 'text.secondary' }}>Please select clusters from the filter above to view their metrics.</Typography>)}
                {!loadingData && !errorLoading && selectedClusterNames.length === 0 && (<Typography sx={{ textAlign: 'center', py: 5, fontFamily: 'Gilroy-Regular', color: 'text.secondary' }}>Please select clusters in the "Resource Health Overview" first.</Typography>)}
                {!loadingData && !errorLoading && selectedClustersForInsights.length > 0 && (
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={0} sx={{ p: 2, border: '1px solid #f0f0f0', borderRadius: '8px', height: chartHeight }}>
                                <Typography variant="subtitle1" sx={{ fontFamily: "Gilroy-SemiBold", mb: 2, fontSize: '1rem' }}>CPU Usage</Typography>
                                <Box sx={{ height: `calc(${chartHeight} - 48px)` }}>
                                    <Line options={lineChartOptions} data={cpuChartData} />
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                             <Paper elevation={0} sx={{ p: 2, border: '1px solid #f0f0f0', borderRadius: '8px', height: chartHeight }}>
                                <Typography variant="subtitle1" sx={{ fontFamily: "Gilroy-SemiBold", mb: 2, fontSize: '1rem' }}>Memory Usage</Typography>
                                <Box sx={{ height: `calc(${chartHeight} - 48px)` }}>
                                    <Bar options={memoryChartOptions} data={memoryChartData} />
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                )}
            </Paper>

            {/* Application Insights Section */}
            <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "12px", border: '1px solid #e0e0e0', backgroundColor: 'white', mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
                    <Box><Typography variant="h6" sx={{ fontFamily: "Gilroy-Bold" }}>Application Insights</Typography><Typography variant="body2" color="text.secondary" sx={{ fontFamily: "Gilroy-Regular", fontSize: '0.875rem' }}>Showing for {datePeriodText}</Typography></Box>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        <FormControl sx={{ minWidth: 220, width: '100%', maxWidth: { sm: 250 } }} size="small">
                            <Select /* Application Dropdown */
                                multiple displayEmpty
                                value={selectedApplicationIdsForAppInsights}
                                onOpen={handleAppInsightsAppDropdownOpen}
                                onClose={handleAppInsightsAppDropdownClose}
                                open={isAppInsightsAppDropdownOpen}
                                input={<OutlinedInput notched={false} />} IconComponent={KeyboardArrowDownIcon}
                                sx={{ ...generalOutlinedButtonStyle, width: '100%', height: '40px', '& .MuiSelect-select': { py: '8.5px', px: 1.5, overflow: 'hidden', textOverflow: 'ellipsis' }, '& .MuiSvgIcon-root': { color: 'black', right: 12 } }}
                                renderValue={(selectedAppIds) => {
                                    if (!selectedClustersForInsights.length) return <Typography sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', color: 'grey.700' }}>Select cluster(s) first</Typography>;
                                    if (!availableApplicationsForAppInsights.length) return <Typography sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', color: 'grey.700' }}>No applications available</Typography>;
                                    if (selectedAppIds.length === 0) return <Typography sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', color: 'grey.700' }}>Select Applications</Typography>;
                                    const displayCount = 1; const names = selectedAppIds.map(id => availableApplicationsForAppInsights.find(app => app.id === id)?.name || id);
                                    const displayedNames = names.slice(0, displayCount).join(', ');
                                    let displayText = displayedNames;
                                    if (names.length > displayCount) displayText += `, ... (+${names.length - displayCount})`;
                                    return <Typography noWrap title={names.join(', ')} sx={{ fontFamily: 'Gilroy-Bold', fontSize: '14px' }}>{displayText}</Typography>;
                                }}
                                MenuProps={{ autoFocus: false, PaperProps: { sx: { width: { xs: '90%', sm: 300 }, maxHeight: 400 } } }}
                                disabled={!selectedClustersForInsights.length || loadingData} 
                            >
                                <ListSubheader sx={{ bgcolor: 'background.paper', p: 1, pt: 1.5, top: -8, borderBottom: '1px solid #eee' }}>
                                    <TextField size="small" fullWidth placeholder="Search applications" value={appInsightsAppSearchTerm} onChange={handleAppInsightsAppSearchChange} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} InputProps={{ startAdornment: <InputAdornment position="start" sx={{ pl: 0, pr: 1, mt: '0 !important' }}><SearchIcon fontSize="small" /></InputAdornment>, endAdornment: appInsightsAppSearchTerm ? <InputAdornment position="end"><IconButton size="small" onClick={(e) => { e.stopPropagation(); setAppInsightsAppSearchTerm(''); }}><CloseIcon fontSize="small" /></IconButton></InputAdornment> : null, sx: { fontSize: '0.875rem', borderRadius: '6px', fontFamily: 'Gilroy-Regular', pl: 1 } }} variant="outlined" autoFocus />
                                </ListSubheader>
                                <Box sx={{ maxHeight: 250, overflow: 'auto', pl: 0.5, pr: 0.5, pt: 0.5 }}>
                                    {filteredApplicationsForAppInsightsDropdown.map((app: ApplicationData) => (
                                        <MenuItem key={app.id} value={app.id} onClick={() => handleStagedAppInsightsAppSelectionChange(app.id)} sx={{ py: 0.3, borderRadius: '4px', m: 0.5, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}>
                                            <Checkbox checked={stagedApplicationIdsForAppInsights.includes(app.id)} size="small" sx={{ p: 0.5, mr: 1, '&.Mui-checked': { color: 'black' } }} />
                                            <ListItemText primary={`${app.name} (${app.clusterName?.replace("Cluster_", "") || 'Unknown Cluster'})`} primaryTypographyProps={{ style: { fontSize: '0.875rem', fontFamily: 'Gilroy-Regular' } }} />
                                        </MenuItem>
                                    ))}
                                    {filteredApplicationsForAppInsightsDropdown.length === 0 && (<MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}><Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>{availableApplicationsForAppInsights.length === 0 ? "No applications in selected cluster(s)" : "No matches found"}</Typography></MenuItem>)}
                                </Box>
                                <Box sx={{ position: 'sticky', bottom: 0, left: 0, right: 0, zIndex: 1, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider', p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '11px' }}>{stagedApplicationIdsForAppInsights.length} Selected</Typography>
                                    <Button size="small" variant="contained" onClick={handleApplyAppInsightsAppSelection} sx={{ ...generalContainedButtonStyle, height: 'auto', textTransform: 'uppercase', fontSize: '0.70rem', px: 1.5, py: 0.7 }}>Apply</Button>
                                </Box>
                            </Select>
                        </FormControl>

                        <FormControl sx={{ minWidth: 220, width: '100%', maxWidth: { sm: 250 } }} size="small">
                            <Select /* Service Dropdown */
                                multiple displayEmpty
                                value={selectedServiceIdsForAppInsights}
                                onOpen={handleAppInsightsServiceDropdownOpen}
                                onClose={handleAppInsightsServiceDropdownClose}
                                open={isAppInsightsServiceDropdownOpen}
                                input={<OutlinedInput notched={false} />} IconComponent={KeyboardArrowDownIcon}
                                sx={{ ...generalOutlinedButtonStyle, width: '100%', height: '40px', '& .MuiSelect-select': { py: '8.5px', px: 1.5, overflow: 'hidden', textOverflow: 'ellipsis' }, '& .MuiSvgIcon-root': { color: 'black', right: 12 } }}
                                renderValue={(selectedIds) => {
                                    if (!activeApplicationIdForServiceFilter) return <Typography sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', color: 'grey.700' }}>Select an active application</Typography>;
                                    if (!availableServicesForAppInsights.length) return <Typography sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', color: 'grey.700' }}>No services in application</Typography>;
                                    if (selectedIds.length === 0) return <Typography sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', color: 'grey.700' }}>Select Services</Typography>;
                                    if (selectedIds.length === availableServicesForAppInsights.length) return <Typography sx={{ fontFamily: 'Gilroy-Bold', fontSize: '14px' }}>All Services ({selectedIds.length})</Typography>;
                                    const dC = 1; const names = selectedIds.map(id => availableServicesForAppInsights.find(s => s.id === id)?.name || id);
                                    const dN = names.slice(0, dC).join(', '); let dT = dN; if (names.length > dC) dT += `, ... (+${names.length - dC})`;
                                    return <Typography noWrap title={names.join(', ')} sx={{ fontFamily: 'Gilroy-Bold', fontSize: '14px' }}>{dT}</Typography>;
                                }}
                                MenuProps={{ autoFocus: false, PaperProps: { sx: { width: { xs: '90%', sm: 300 }, maxHeight: 400 } } }}
                                disabled={!activeApplicationIdForServiceFilter || !availableServicesForAppInsights.length || loadingData}
                            >
                                <ListSubheader sx={{ bgcolor: 'background.paper', p: 1, pt: 1.5, top: -8, borderBottom: '1px solid #eee' }}>
                                    <TextField size="small" fullWidth placeholder="Search services" value={appInsightsServiceSearchTerm} onChange={handleAppInsightsServiceSearchChange} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} InputProps={{ startAdornment: <InputAdornment position="start" sx={{ pl: 0, pr: 1, mt: '0 !important' }}><SearchIcon fontSize="small" /></InputAdornment>, endAdornment: appInsightsServiceSearchTerm ? <InputAdornment position="end"><IconButton size="small" onClick={(e) => { e.stopPropagation(); setAppInsightsServiceSearchTerm(''); }}><CloseIcon fontSize="small" /></IconButton></InputAdornment> : null, sx: { fontSize: '0.875rem', borderRadius: '6px', fontFamily: 'Gilroy-Regular', pl: 1 } }} variant="outlined" autoFocus />
                                </ListSubheader>
                                <Box sx={{ maxHeight: 250, overflow: 'auto', pl: 0.5, pr: 0.5, pt: 0.5 }}>
                                    {filteredServicesForAppInsightsDropdown.map((service) => (<MenuItem key={service.id} value={service.id} onClick={() => handleStagedAppInsightsServiceSelectionChange(service.id)} sx={{ py: 0.3, borderRadius: '4px', m: 0.5, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}><Checkbox checked={stagedServiceIdsForAppInsights.includes(service.id)} size="small" sx={{ p: 0.5, mr: 1, '&.Mui-checked': { color: 'black' } }} /><ListItemText primary={service.name} primaryTypographyProps={{ style: { fontSize: '0.875rem', fontFamily: 'Gilroy-Regular' } }} /></MenuItem>))}
                                    {filteredServicesForAppInsightsDropdown.length === 0 && (<MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}><Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>{availableServicesForAppInsights.length === 0 ? "No services available" : "No matches found"}</Typography></MenuItem>)}
                                </Box>
                                <Box sx={{ position: 'sticky', bottom: 0, left: 0, right: 0, zIndex: 1, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider', p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '11px' }}>{stagedServiceIdsForAppInsights.length} / {availableServicesForAppInsights.length} Selected</Typography>
                                    <Button size="small" variant="contained" onClick={handleApplyAppInsightsServiceSelection} sx={{ ...generalContainedButtonStyle, height: 'auto', textTransform: 'uppercase', fontSize: '0.70rem', px: 1.5, py: 0.7 }}>Apply</Button>
                                </Box>
                            </Select>
                        </FormControl>
                    </Stack>
                </Box>

                {/* Application Chips Display (styled Buttons) */}
                {selectedApplicationIdsForAppInsights.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, justifyContent: 'flex-start', alignContent: 'flex-start' }}>
                        {selectedApplicationIdsForAppInsights.map(appId => {
                            const appData = availableApplicationsForAppInsights.find(app => app.id === appId);
                            if (!appData) return null;
                            const isActive = activeApplicationIdForServiceFilter === appId;
                            return (
                                <Button
                                    key={appId}
                                    onClick={() => handleActiveAppChipClick(appId)}
                                    variant="outlined"
                                    sx={{
                                        borderRadius: "20px",
                                        textTransform: "none",
                                        fontWeight: 500,
                                        fontSize: "12px",
                                        padding: "4px 12px",
                                        borderWidth: "1px",
                                        backgroundColor: isActive ? "#F5F5F5" : "transparent",
                                        color: "#333",
                                        borderColor: isActive ? "#000" : "#DADADA",
                                        "&:hover": {
                                            backgroundColor: "#EAEAEA",
                                            borderColor: "#000",
                                        },
                                        fontFamily: 'Gilroy-SemiBold',
                                    }}
                                >
                                    {appData.name}
                                </Button>
                            );
                        })}
                    </Box>
                )}

                {/* Health Bar for Active Application's Services */}
                {activeApplicationIdForServiceFilter && (() => {
                    const activeApp = availableApplicationsForAppInsights.find(app => app.id === activeApplicationIdForServiceFilter);

                    if (!activeApp) {
                        return null;
                    }
                    const servicesForActiveApp = availableServicesForAppInsights;

                    let numHealthyServices = 0;
                    let numUnhealthyServices = 0; 

                    servicesForActiveApp.forEach(service => {
                        if (service.health && service.health.healthy >= 70) { 
                            numHealthyServices++;
                        } else {
                            numUnhealthyServices++;
                        }
                    });

                    const totalServicesCount = numHealthyServices + numUnhealthyServices;

                    const showHealthySegment = numHealthyServices > 0;
                    const showUnhealthySegment = numUnhealthyServices > 0; 
                    const showGap = showHealthySegment && showUnhealthySegment; 
                    const gapWidthPx = 2;

                    const isAppOverallHealthy = activeApp.health && activeApp.health.healthy >= 70; 
                    const healthyIconUrl = "/assets/tickcircle.svg";
                    const unhealthyIconUrl = "/assets/close-circle-fill.svg";

                    return (
                        <Box sx={{ mt: 2.5, mb: 2.5, width: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="h6" sx={{ fontFamily: "Gilroy-Bold", fontSize: '1.1rem', lineHeight: 1.3, mr: 1.5 }}>
                                        {activeApp.name}
                                    </Typography>
                                    <Chip
                                        icon={<img src={isAppOverallHealthy ? healthyIconUrl : unhealthyIconUrl} alt={isAppOverallHealthy ? "Healthy" : "Unhealthy"} style={{ width: '12px', height: '12px' }} />}
                                        label={isAppOverallHealthy ? 'Healthy' : 'Unhealthy'} 
                                        size="small"
                                        sx={{
                                            height: '18px',
                                            fontSize: '10px',
                                            fontFamily: 'Gilroy-SemiBold',
                                            bgcolor: isAppOverallHealthy ? '#12B76A33' : '#F0443833',
                                            color: isAppOverallHealthy ? '#12B76A' : '#F04438',
                                            '.MuiChip-label': { px: '4px', py: '2px', lineHeight: '1' },
                                            '.MuiChip-icon': { ml: '4px', mr: '-2px', fontSize: '0.7rem', display: 'flex', alignItems: 'center' },
                                        }}
                                    />
                                </Box>
                                {servicesForActiveApp.length > 0 && (
                                    <Typography sx={{ fontFamily: "Gilroy-SemiBold", fontSize: '14px', color: '#232324' }}>
                                        Total Services: {totalServicesCount}
                                    </Typography>
                                )}
                            </Box>

                            {servicesForActiveApp.length > 0 && (
                                <Box sx={{
                                    flexGrow: 1,
                                    display: 'flex',
                                    height: '12px',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    border: '1px solid #e7e7e7',
                                    mb: 1,
                                }}>
                                    {showHealthySegment && (
                                        <Box sx={{ flexGrow: numHealthyServices, flexBasis: 0, backgroundColor: '#12B76A' }} />
                                    )}
                                    {showGap && ( 
                                        <Box sx={{ width: `${gapWidthPx}px`, flexShrink: 0, backgroundColor: 'white' }} />
                                    )}
                                    {showUnhealthySegment && ( 
                                        <Box sx={{ flexGrow: numUnhealthyServices, flexBasis: 0, backgroundColor: '#F04438' }} />
                                    )}
                                    {(!showHealthySegment && !showUnhealthySegment && totalServicesCount > 0) && ( 
                                        <Box sx={{ flexGrow: 1, backgroundColor: '#e0e0e0' }} />
                                    )}
                                </Box>
                            )}

                            {servicesForActiveApp.length > 0 && (
                                <Stack direction="row" spacing={2.5} justifyContent="flex-end" alignItems="center">
                                    {(showHealthySegment || numHealthyServices >= 0) && (
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Box sx={{ width: '16px', height: '16px', borderRadius: '50%', bgcolor: '#12B76A', mr: 0.75 }} />
                                            <Typography variant="caption" sx={{ fontFamily: "Gilroy-Medium", fontSize: '14px', color: '#232324', whiteSpace: 'nowrap', mr: 0.25 }}>
                                                Healthy:
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontFamily: "Gilroy-SemiBold", fontSize: '14px', color: '#232324', whiteSpace: 'nowrap' }}>
                                                {numHealthyServices}
                                            </Typography>
                                        </Box>
                                    )}
                                    {(showUnhealthySegment || numUnhealthyServices > 0) && ( 
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Box sx={{ width: '16px', height: '16px', borderRadius: '50%', bgcolor: '#F04438', mr: 0.75 }} />
                                            <Typography variant="caption" sx={{ fontFamily: "Gilroy-Medium", fontSize: '14px', color: '#232324', whiteSpace: 'nowrap', mr: 0.25 }}>
                                                Critical:
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontFamily: "Gilroy-SemiBold", fontSize: '14px', color: '#232324', whiteSpace: 'nowrap' }}>
                                                {numUnhealthyServices}
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>
                            )}
                            {activeApp && servicesForActiveApp.length === 0 && !loadingData && ( 
                                <Typography sx={{ textAlign: 'left', py: 1, fontFamily: 'Gilroy-Regular', color: 'text.secondary', fontSize: '0.8rem', mt: 0.5 }}>
                                    No services found for this application.
                                </Typography>
                            )}
                        </Box>
                    );
                })()}

                {/* Service Details List */}
                {activeApplicationIdForServiceFilter && serviceListData.length > 0 && !loadingData && (
                    <Box sx={{ mt: 3, borderTop: '1px solid #e0e0e0', }}>
                        <Box sx={{ display: 'flex', py: 1, bgcolor: '#F2F2F2', px: { xs: 1, md: 2 } }}>
                            <Typography variant="caption" sx={{ width: { xs: '25%', md: '25%' }, fontWeight: 400, color: '#666666', fontFamily: 'Gilroy-Medium', pr: 1 }}>SERVICE NAME</Typography>
                            <Typography variant="caption" sx={{ width: { xs: '20%', md: '15%' }, fontWeight: 400, color: '#666666', fontFamily: 'Gilroy-Medium', textAlign: 'left', pr: 1 }}>POD STATUS</Typography>
                            <Typography variant="caption" sx={{ width: { xs: '15%', md: '15%' }, fontWeight: 400, color: '#666666', fontFamily: 'Gilroy-Medium', textAlign: 'left', pr: 1 }}>REPLICAS</Typography>
                            <Typography variant="caption" sx={{ width: { xs: '20%', md: '15%' }, fontWeight: 400, color: '#666666', fontFamily: 'Gilroy-Medium', textAlign: 'left', pr: 1 }}>DEPLOYMENT</Typography>
                            <Typography variant="caption" sx={{ width: { xs: '20%', md: '15%' }, fontWeight: 400, color: '#666666', fontFamily: 'Gilroy-Medium', textAlign: 'center', pr: 1 }}>CPU USAGE</Typography>
                            <Typography variant="caption" sx={{ width: { xs: '20%', md: '15%' }, fontWeight: 400, color: '#666666', fontFamily: 'Gilroy-Medium', textAlign: 'center' }}>MEMORY USAGE</Typography>
                        </Box>

                        {serviceListData.map(service => {
                            const isServiceHealthy = service.health.healthy >= 70; 
                            const healthyIconUrl = "/assets/tickcircle.svg";
                            const criticalIconUrl = "/assets/close-circle-fill.svg"; 

                            return (
                                <Box
                                    key={service.id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        py: 1.5,
                                        px: { xs: 1, md: 2 },
                                        borderBottom: 1,
                                        borderColor: 'grey.200',
                                        '&:last-child': { borderBottom: 0 },
                                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.01)' }
                                    }}
                                >
                                    <Box sx={{
                                        width: { xs: '25%', md: '25%' },
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        pr: 1.5,
                                        overflow: 'hidden'
                                    }}>
                                        <Chip
                                            icon={<img src={isServiceHealthy ? healthyIconUrl : criticalIconUrl} alt={isServiceHealthy ? "Healthy" : "Critical"} style={{ width: '12px', height: '12px' }} />}
                                            label={isServiceHealthy ? 'Healthy' : 'Critical'} 
                                            size="small"
                                            sx={{
                                                height: '18px',
                                                fontSize: '10px',
                                                fontFamily: 'Gilroy-SemiBold',
                                                bgcolor: isServiceHealthy ? '#12B76A33' : '#F0443833',
                                                color: isServiceHealthy ? '#12B76A' : '#F04438',
                                                '.MuiChip-label': { px: '6px', lineHeight: '1' },
                                                '.MuiChip-icon': { ml: '6px', mr: '-2px', fontSize: '12px', height: '10px', display: 'flex', alignItems: 'center' },
                                                alignSelf: 'flex-start',
                                                mb: 0.25,
                                                borderRadius: '24px',
                                                width: 'fit-content',
                                            }}
                                        />
                                        <Typography variant="body2" title={service.name} sx={{ fontFamily: 'Gilroy-SemiBold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '14px', textAlign: 'left' }}>
                                            {service.name}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ width: { xs: '20%', md: '15%' }, px: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                        <Typography variant="body2" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', textAlign: 'left' }}>{service.podStatus}</Typography>
                                    </Box>

                                    <Box sx={{ width: { xs: '15%', md: '15%' }, px: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                        <Typography variant="body2" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', textAlign: 'left' }}>{service.replicas}</Typography>
                                    </Box>

                                    <Box sx={{ width: { xs: '20%', md: '15%' }, px: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                        <img src={healthyIconUrl} alt="Updated" style={{ width: '16px', height: '16px', color: '#12B76A' }} />
                                        <Typography variant="body2" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', textAlign: 'left', ml: '2px' }}>{service.deploymentStatus}</Typography>
                                    </Box>

                                    <Box sx={{ width: { xs: '20%', md: '15%' }, px: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        {service.healthMetrics && chartTimePoints && <MiniBarChart
                                            data={service.healthMetrics.cpuUsage}
                                            maxVal={service.healthMetrics.maxCpu}
                                            barColor="#2B7FFF"
                                            hoverColor="#1A6FEA"
                                            chartTimePoints={chartTimePoints}
                                            metricType="CPU"
                                            onBarEnter={handleMiniBarMouseEnter}
                                            onBarLeave={handleMiniBarMouseLeave}
                                        />}
                                    </Box>

                                    <Box sx={{ width: { xs: '20%', md: '15%' }, px: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        {service.healthMetrics && chartTimePoints && <MiniBarChart
                                            data={service.healthMetrics.memoryUsage}
                                            maxVal={service.healthMetrics.maxMemory}
                                            barColor="#2B7FFF"
                                            hoverColor="#1A6FEA"
                                            chartTimePoints={chartTimePoints}
                                            metricType="Memory"
                                            onBarEnter={handleMiniBarMouseEnter}
                                            onBarLeave={handleMiniBarMouseLeave}
                                        />}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
                {activeApplicationIdForServiceFilter && serviceListData.length === 0 && !loadingData && (
                    <Typography sx={{ textAlign: 'center', py: 3, fontFamily: 'Gilroy-Regular', color: 'text.secondary' }}>
                        {availableServicesForAppInsights.length > 0 ? "No services currently selected to display details." : "No services available in this application to display details."}
                    </Typography>
                )}

                {/* Conditional Rendering for Application Insights Section */}
                {loadingData && !activeApplicationIdForServiceFilter && (<Stack alignItems="center" justifyContent="center" sx={{ minHeight: '100px', py: 3 }}><CircularProgress size={24} /><Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Loading...</Typography></Stack>)}
                {!loadingData && errorLoading && (<Alert severity="error" sx={{ mt: 2 }}>Failed to load application data: {errorLoading}</Alert>)}
                {!loadingData && !errorLoading && selectedClustersForInsights.length === 0 && (<Typography sx={{ textAlign: 'center', py: 5, fontFamily: 'Gilroy-Regular', color: 'text.secondary' }}>Please select cluster(s) in "Cluster Insights" to see applications.</Typography>)}
                {!loadingData && !errorLoading && selectedClustersForInsights.length > 0 && availableApplicationsForAppInsights.length === 0 && (<Typography sx={{ textAlign: 'center', py: 5, fontFamily: 'Gilroy-Regular', color: 'text.secondary' }}>No applications found in the selected cluster(s).</Typography>)}
                {!loadingData && !errorLoading && availableApplicationsForAppInsights.length > 0 && selectedApplicationIdsForAppInsights.length === 0 && (<Typography sx={{ textAlign: 'center', py: 5, fontFamily: 'Gilroy-Regular', color: 'text.secondary' }}>Please select application(s) from the dropdown to see details.</Typography>)}
                {!loadingData && !errorLoading && selectedApplicationIdsForAppInsights.length > 0 && !activeApplicationIdForServiceFilter && (<Typography sx={{ textAlign: 'center', py: 5, fontFamily: 'Gilroy-Regular', color: 'text.secondary' }}>Please select an active application chip to view its services.</Typography>)}

                {!loadingData && !errorLoading && activeApplicationIdForServiceFilter && selectedServiceIdsForAppInsights.length === 0 && availableServicesForAppInsights.length > 0 && serviceListData.length === 0 && (
                    <Typography sx={{ textAlign: 'center', py: 5, fontFamily: 'Gilroy-Regular', color: 'text.secondary' }}>
                        Please select service(s) from the dropdown above to view their details.
                    </Typography>
                )}
            </Paper>


            <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "12px", border: '1px solid #e0e0e0', backgroundColor: 'white', mb: 3 }}>
                <Box sx={{ mb: 2.5 }}>
                    <Typography variant="h6" sx={{ fontFamily: "Gilroy-Bold" }}>APIs Key Performance Indicators</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "Gilroy-Regular", fontSize: '0.875rem' }}>
                        Metrics for {datePeriodText}
                    </Typography>
                </Box>

                {loadingData ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '100px' }}><CircularProgress /></Stack>
                ) : !errorLoading && selectedClustersForInsights.length > 0 ? (
                    <Box sx={{
                        backgroundColor: '#F9FBFC',
                        borderRadius: '8px',
                        p: 3,
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-around', 
                            alignItems: 'center',
                            width: '100%'
                        }}>
                            {kpiData.map((kpi, index) => (
                                <React.Fragment key={kpi.title}>
                                    <KpiCard
                                        title={kpi.title}
                                        value={kpi.value}
                                        change={kpi.change}
                                        isPositive={kpi.isPositive}
                                        icon={kpi.icon}
                                    />
                                    {index < kpiData.length - 1 && (
                                        <Divider orientation="vertical" flexItem sx={{ mx: 1, color:'#E4E7EC' }} />
                                    )}
                                </React.Fragment>
                            ))}
                        </Box>
                    </Box>
                ) : (
                    <Typography sx={{ textAlign: 'center', py: 5, fontFamily: 'Gilroy-Regular', color: 'text.secondary' }}>
                        Please select clusters from the filter above to view their metrics.
                    </Typography>
                )}
            </Paper>

            <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "12px", border: '1px solid #e0e0e0', backgroundColor: 'white', mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                        <Typography variant="h6" sx={{ fontFamily: "Gilroy-Bold" }}>API Traffic Insights</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "Gilroy-Regular", fontSize: '0.875rem' }}>
                            Showing for {datePeriodText}
                        </Typography>
                    </Box>
                    <FormControl sx={{ minWidth: 240, maxWidth: { xs: '100%', sm: 300 } }} size="small">
                        <Select
                            value={selectedApiForTraffic}
                            onChange={(e: SelectChangeEvent<string>) => setSelectedApiForTraffic(e.target.value)}
                            input={<OutlinedInput notched={false} />}
                            IconComponent={KeyboardArrowDownIcon}
                            sx={{ ...generalOutlinedButtonStyle, width: '100%', height: '40px', '& .MuiSelect-select': { py: '8.5px', px: 1.5 }, '& .MuiSvgIcon-root': { color: 'black', right: 12 } }}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                        >
                            {DUMMY_APIS.map(apiName => (
                                <MenuItem key={apiName} value={apiName}>
                                    <ListItemText primary={apiName} primaryTypographyProps={{ style: { fontSize: '0.875rem', fontFamily: 'Gilroy-Regular', overflow: 'hidden', textOverflow: 'ellipsis' } }} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={9}>
                        <Paper elevation={0} sx={{ p: 2, border: '1px solid #f0f0f0', borderRadius: '8px', height: chartHeight }}>
                            <Typography variant="subtitle1" sx={{ fontFamily: "Gilroy-SemiBold", mb: 2, fontSize: '1rem' }}>
                                API Traffic
                            </Typography>
                            <Box sx={{ height: `calc(${chartHeight} - 48px)` }}>
                                <Line options={buyloadsTrafficChartOptions} data={buyloadsTrafficChartData} />
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Paper elevation={0} sx={{ p: 2, border: '1px solid #f0f0f0', borderRadius: '8px', height: chartHeight }}>
                            <Typography variant="subtitle1" sx={{ fontFamily: "Gilroy-SemiBold", mb: 2, fontSize: '1rem' }}>
                                API Call Stats
                            </Typography>
                            <Box sx={{ height: `calc(${chartHeight} - 48px)` }}>
                                <Doughnut options={buyloadStatsChartOptions} data={buyloadStatsChartData} />
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Paper>

            <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "12px", border: '1px solid #e0e0e0', backgroundColor: 'white', mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                        <Typography variant="h6" sx={{ fontFamily: "Gilroy-Bold" }}>
                            API Stats
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "Gilroy-Regular", fontSize: '0.875rem' }}>
                            Showing latest API performance data
                        </Typography>
                    </Box>
                    <Stack direction={{ xs: "column", sm:"row" }} spacing={2} alignItems="center" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    </Stack>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', py: 1, bgcolor: '#F2F2F2', px: { xs: 1, md: 2 }, borderRadius: '6px' }}>
                        <Typography variant="caption" sx={{ width: { xs: '30%', md: '30%' }, fontWeight: 400, color: '#666666', fontFamily: 'Gilroy-Medium', pr: 1 }}>API NAME</Typography>
                        <Typography variant="caption" sx={{ width: '14%', fontWeight: 400, color: '#666666', fontFamily: 'Gilroy-Medium', textAlign: 'left', pr: 1 }}>TOTAL</Typography>
                        <Typography variant="caption" sx={{ width: '14%', fontWeight: 400, color: '#666666', fontFamily: 'Gilroy-Medium', textAlign: 'left', pr: 1 }}>SUCCESS</Typography>
                        <Typography variant="caption" sx={{ width: '14%', fontWeight: 400, color: '#666666', fontFamily: 'Gilroy-Medium', textAlign: 'left', pr: 1 }}>FAILED</Typography>
                        <Typography variant="caption" sx={{ width: '14%', fontWeight: 400, color: '#666666', fontFamily: 'Gilroy-Medium', textAlign: 'center', pr: 1 }}>AVG. TAT</Typography>
                        <Typography variant="caption" sx={{ width: '14%', fontWeight: 400, color: '#666666', fontFamily: 'Gilroy-Medium', textAlign: 'center' }}>ERROR RATE</Typography>
                    </Box>

                    {apiStatsData.map((row) => {
                        const isCritical = row.errorRate.value > 10;
                        const healthyIconUrl = "/assets/tickcircle.svg";
                        const criticalIconUrl = "/assets/close-circle-fill.svg";

                        return (
                            <Box
                                key={row.apiName}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    py: 1.5,
                                    px: { xs: 1, md: 2 },
                                    borderBottom: 1,
                                    borderColor: 'grey.200',
                                    '&:last-child': { borderBottom: 0 },
                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.01)' }
                                }}
                            >
                                <Box sx={{ width: { xs: '30%', md: '30%' }, display: 'flex', flexDirection: 'column', justifyContent: 'center', pr: 1.5, overflow: 'hidden' }}>
                                    <Chip
                                        icon={<img src={!isCritical ? healthyIconUrl : criticalIconUrl} alt={!isCritical ? "Healthy" : "Critical"} style={{ width: '12px', height: '12px' }} />}
                                        label={!isCritical ? 'Healthy' : 'Critical'}
                                        size="small"
                                        sx={{
                                            height: '18px', fontSize: '10px', fontFamily: 'Gilroy-SemiBold',
                                            bgcolor: !isCritical ? '#12B76A33' : '#F0443833',
                                            color: !isCritical ? '#12B76A' : '#F04438',
                                            '.MuiChip-label': { px: '6px', lineHeight: '1' },
                                            '.MuiChip-icon': { ml: '6px', mr: '-2px' },
                                            alignSelf: 'flex-start', mb: 0.25, borderRadius: '24px',
                                        }}
                                    />
                                    <Typography variant="body2" title={row.apiName} sx={{ fontFamily: 'Gilroy-SemiBold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '14px', textAlign: 'left' }}>
                                        {row.apiName}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ width: '14%', px: 1, fontFamily: 'Gilroy-Regular', fontSize: '14px', textAlign: 'left' }}>{row.totalTransaction}</Typography>
                                <Typography variant="body2" sx={{ width: '14%', px: 1, fontFamily: 'Gilroy-Regular', fontSize: '14px', textAlign: 'left' }}>{row.totalSuccess}</Typography>
                                <Typography variant="body2" sx={{ width: '14%', px: 1, fontFamily: 'Gilroy-Regular', fontSize: '14px', textAlign: 'left' }}>{row.totalFailed}</Typography>
                                <Typography variant="body2" sx={{ width: '14%', px: 1, fontFamily: 'Gilroy-Regular', fontSize: '14px', textAlign: 'center' }}>{row.avgTat}</Typography>
                                <Typography variant="body2" sx={{ width: '14%', px: 1, fontFamily: 'Gilroy-SemiBold', fontSize: '14px', textAlign: 'center', color: isCritical ? 'error.main' : 'inherit' }}>
                                    {row.errorRate.display}
                                </Typography>
                            </Box>
                        );
                    })}

                    {(!apiStatsData || apiStatsData.length === 0) && (
                        <Typography sx={{ textAlign: 'center', py: 5, fontFamily: 'Gilroy-Regular', color: 'text.secondary' }}>
                            No API statistics to display.
                        </Typography>
                    )}
                </Box>
            </Paper>
            
            <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "12px", border: '1px solid #e0e0e0', backgroundColor: 'white', mb: 3 }}>
                <Box sx={{ mb: 2.5 }}>
                    <Typography variant="h6" sx={{ fontFamily: "Gilroy-Bold" }}>WAF Bot Detection</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "Gilroy-Regular", fontSize: '0.875rem' }}>
                        Showing for {datePeriodText}
                    </Typography>
                </Box>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8.4}>
                        <Paper elevation={0} sx={{ p: 2, border: '1px solid #f0f0f0', borderRadius: '8px', height: chartHeight }}>
                            <Typography variant="subtitle1" sx={{ fontFamily: "Gilroy-SemiBold", mb: 2, fontSize: '1rem' }}>
                                Bot Traffic Over Time
                            </Typography>
                            <Box sx={{ height: `calc(${chartHeight} - 48px)` }}>
                                <Line options={wafBotLineChartOptions} data={wafBotLineChartData} />
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={3.6}>
                        <Paper elevation={0} sx={{ p: 2, border: '1px solid #f0f0f0', borderRadius: '8px', height: chartHeight }}>
                            <Typography variant="subtitle1" sx={{ fontFamily: "Gilroy-SemiBold", mb: 2, fontSize: '1rem' }}>
                                Bot Distribution
                            </Typography>
                            <Box sx={{ height: `calc(${chartHeight} - 48px)` }}>
                                <Doughnut options={wafBotDoughnutChartOptions} data={wafBotDoughnutChartData} />
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Paper>
            
            <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "12px", border: '1px solid #e0e0e0', backgroundColor: 'white', mb: 3 }}>
                <Box sx={{ mb: 2.5 }}>
                    <Typography variant="h6" sx={{ fontFamily: "Gilroy-Bold" }}>WAF Request Insights</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "Gilroy-Regular", fontSize: '0.875rem' }}>
                        Showing for {datePeriodText}
                    </Typography>
                </Box>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={7.2}>
                        <Paper elevation={0} sx={{ p: 2, border: '1px solid #f0f0f0', borderRadius: '8px', height: chartHeight }}>
                            <Typography variant="subtitle1" sx={{ fontFamily: "Gilroy-SemiBold", mb: 2, fontSize: '1rem' }}>
                                Allowed Requests
                            </Typography>
                            <Box sx={{ height: `calc(${chartHeight} - 48px)` }}>
                                <Line options={wafRequestChartOptions} data={allowedRequestsChartData} />
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4.8}>
                        <Paper elevation={0} sx={{ p: 2, border: '1px solid #f0f0f0', borderRadius: '8px', height: chartHeight }}>
                            <Typography variant="subtitle1" sx={{ fontFamily: "Gilroy-SemiBold", mb: 2, fontSize: '1rem' }}>
                                Counted Requests
                            </Typography>
                            <Box sx={{ height: `calc(${chartHeight} - 48px)` }}>
                                <Line options={wafRequestChartOptions} data={countedRequestsChartData} />
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Paper>

            {/* NEW SECTION: LLM OBSERVABILITY INSIGHTS */}
            <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "12px", border: '1px solid #e0e0e0', backgroundColor: 'white', mb: 3 }}>
                <Box sx={{ mb: 2.5 }}>
                    <Typography variant="h6" sx={{ fontFamily: "Gilroy-Bold" }}>LLM Observability Insights</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "Gilroy-Regular", fontSize: '0.875rem' }}>
                        Gain visibility into your LLM spends and token usage for {datePeriodText}
                    </Typography>
                </Box>
                
                {/* 1. KPIs */}
                <Box sx={{ backgroundColor: '#F9FBFC', borderRadius: '8px', p: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', alignItems: 'center', width: '100%' }}>
                        <KpiCard title="Total Tokens" value="237,450" change="+12.4%" isPositive={true} icon={<ReceiptLongIcon />} />
                        <Divider orientation="vertical" flexItem sx={{ mx: 1, color:'#E4E7EC' }} />
                        <KpiCard title="Total Cost" value="$145.20" change="-4.1%" isPositive={true} icon={<TrendingUpIcon />} />
                        <Divider orientation="vertical" flexItem sx={{ mx: 1, color:'#E4E7EC' }} />
                        <KpiCard title="LLM Calls" value="4,520" change="+8.9%" isPositive={true} icon={<CheckCircleOutlineIcon />} />
                    </Box>
                </Box>

                <Grid container spacing={3}>
                    {/* 2. LLM Usage Chart (Line) */}
                    <Grid item xs={12} md={8}>
                        <Paper elevation={0} sx={{ p: 2, border: '1px solid #f0f0f0', borderRadius: '8px', height: chartHeight }}>
                            <Typography variant="subtitle1" sx={{ fontFamily: "Gilroy-SemiBold", mb: 2, fontSize: '1rem' }}>
                                Token Usage Over Time
                            </Typography>
                            <Box sx={{ height: `calc(${chartHeight} - 48px)` }}>
                                <Line options={llmUsageChartOptions} data={llmUsageLineChartData} />
                            </Box>
                        </Paper>
                    </Grid>
                    
                    {/* 3. Performance Chart (Bar) */}
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ p: 2, border: '1px solid #f0f0f0', borderRadius: '8px', height: chartHeight }}>
                            <Typography variant="subtitle1" sx={{ fontFamily: "Gilroy-SemiBold", mb: 2, fontSize: '1rem' }}>
                                Avg Request Completion Time
                            </Typography>
                            <Box sx={{ height: `calc(${chartHeight} - 48px)` }}>
                                <Bar options={llmPerformanceBarOptions} data={llmPerformanceBarData} />
                            </Box>
                        </Paper>
                    </Grid>

                    {/* 4. Split Chart (Doughnut) */}
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ p: 2, border: '1px solid #f0f0f0', borderRadius: '8px', height: chartHeight }}>
                            <Typography variant="subtitle1" sx={{ fontFamily: "Gilroy-SemiBold", mb: 2, fontSize: '1rem' }}>
                                LLM Split (Tokens)
                            </Typography>
                            <Box sx={{ height: `calc(${chartHeight} - 48px)` }}>
                                <Doughnut options={buyloadStatsChartOptions} data={llmSplitDoughnutData} />
                            </Box>
                        </Paper>
                    </Grid>

                    {/* 5. Summary List */}
                    <Grid item xs={12} md={8}>
                        <Paper elevation={0} sx={{ p: 2, border: '1px solid #f0f0f0', borderRadius: '8px', height: chartHeight, overflowY: 'auto' }}>
                            <Typography variant="subtitle1" sx={{ fontFamily: "Gilroy-SemiBold", mb: 2, fontSize: '1rem' }}>
                                Summary
                            </Typography>
                            <Stack spacing={2}>
                                {llmSummaryStats.map((item, index) => (
                                    <Paper key={index} elevation={0} sx={{ p: '16px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#F9FAFB' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box sx={{ width: 40, height: 40, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#FFFFFF', color: '#0064FF' }}>
                                                {item.icon}
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.2, fontFamily: 'Gilroy-Medium' }}>{item.label[0]}</Typography>
                                                <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.2, fontFamily: 'Gilroy-Medium' }}>{item.label[1]}</Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                                <Typography sx={{ fontSize:'16px', fontWeight: 600, fontFamily: 'Gilroy-Bold' }}>{item.value}</Typography>
                                                {item.isPositive ? <TrendingDownIcon sx={{ color: '#03A65A', fontSize: '18px' }} /> : <TrendingUpIcon sx={{ color: '#CA2B20', fontSize: '18px' }} />}
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', color: item.isPositive ? '#03A65A' : '#CA2B20', mt: -0.5 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 500, fontFamily: 'Gilroy-Medium' }}>{item.change}</Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, fontFamily: 'Gilroy-Regular' }}>vs last month</Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                ))}
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>
                
                {/* 6. Include the Traces Section Component */}
                <LLMTracesSection />
            </Paper>

            <Popper
                open={miniBarTooltipInfo.visible}
                anchorEl={miniBarTooltipInfo.anchorEl}
                placement="top"
                transition
                modifiers={[{ name: 'offset', options: { offset: [0, 8], }, },]}
                sx={{ zIndex: 1300 }}
            >
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={150}>
                        <Paper elevation={3} sx={{ p: 1, borderRadius: '4px', bgcolor: '#fff', border: '1px solid #eee', pointerEvents: 'none' }}>
                            <Typography variant="caption" display="block" sx={{ fontFamily: 'Gilroy-Regular', color: 'grey.700', textAlign: 'center', mb: 0.5 }}>
                                On {miniBarTooltipInfo.content.date}
                            </Typography>
                            <Typography sx={{ fontFamily: 'Gilroy-Bold', textAlign: 'center', fontSize: '0.875rem' }}>
                                {miniBarTooltipInfo.content.type} Usage :  {miniBarTooltipInfo.content.value}
                            </Typography>
                        </Paper>
                    </Fade>
                )}
            </Popper>
        </Container>
    );
};

const Grafana: React.FC = () => {
    const theme = useTheme();
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(() => {
        if (typeof window !== 'undefined') { const storedState = localStorage.getItem('faradayMainDrawerOpenState'); return storedState !== null ? JSON.parse(storedState) : true; } return true;
    });
    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => { setIsDrawerActuallyOpen(isOpenFromDrawer); if (typeof window !== 'undefined') localStorage.setItem('faradayMainDrawerOpenState', JSON.stringify(isOpenFromDrawer)); };
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;
    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
            {isDrawerActuallyOpen && (
                <MainDrawer
                    index={"insights-observability-dashboard"}
                    onStateChange={handleDrawerStateChange}
                />
            )}
            {!isDrawerActuallyOpen && (
                <MainDrawer
                    index={"insights"}
                    onStateChange={handleDrawerStateChange}
                />
            )}
            <Box component="main" sx={{ flexGrow: 1, p: 0, transition: theme.transitions.create(['margin', 'width'], { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), marginLeft: currentMarginLeft, width: `calc(100vw - ${currentMarginLeft}px)`, overflowX: 'hidden', }}>
                <GrafanaContent />
            </Box>
        </Box>
    );
};
export default Grafana;